import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { Package, Edit, Trash2, Plus, X, Loader2, Image as ImageIcon, PackageCheck, ChevronLeft, ChevronRight, MapPin, FileDown, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { AppBar } from './ui/app-bar';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ProductEntryForm, { ProductEntryFormHandle } from './ProductEntryForm';
import { toast } from './ui/use-toast';
import { useNavigate } from 'react-router-dom';
import AddSerialNumberModal from './AddSerialNumberModal';
import LocationManagementScreen from './LocationManagementScreen';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from './ui/pagination';
import { FaWhatsapp } from 'react-icons/fa';
import { MdMessage } from 'react-icons/md';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { useIsAndroid } from '../hooks/use-android';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useSettings } from '@/hooks/useSettings';
import { getBaseUrl } from '@/lib/api';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';
import { Progress } from './ui/progress';

// Excel product import template headers (exact order for download)
const PRODUCT_IMPORT_HEADERS = [
  'Product Name', 'Price Inc GST', 'Category', 'Barcode', 'CostPrice', 'UQC', 'HSN', 'Brand Name',
  'Color', 'Rack', 'Shelf', 'Size', 'Article Code', 'Basic Price', 'MFG Date', 'ROL',
  'SGST %', 'CGST %', 'IGST %', 'Cess %', 'Opening Stock', 'Location', 'isService', 'isTrackable',
  'Discount %', 'hasSerialNosStr'
];

// Flexible header mapping: Excel column name variants (lowercase, trimmed) -> canonical key for API
const EXCEL_HEADER_MAP: Record<string, string> = {
  'product name': 'Product Name', 'productname': 'Product Name', 'product_name': 'Product Name',
  'price inc gst': 'Price Inc GST', 'mrp': 'Price Inc GST',
  'category': 'Category',
  'barcode': 'Barcode', 'bar code': 'Barcode',
  'costprice': 'CostPrice', 'cost price': 'CostPrice',
  'uqc': 'UQC',
  'hsn': 'HSN',
  'brand name': 'Brand Name', 'brandname': 'Brand Name',
  'color': 'Color',
  'rack': 'Rack',
  'shelf': 'Shelf',
  'size': 'Size',
  'article code': 'Article Code', 'articlecode': 'Article Code',
  'basic price': 'Basic Price', 'basicprice': 'Basic Price',
  'mfg date': 'MFG Date', 'mfd': 'MFG Date',
  'rol': 'ROL',
  'sgst %': 'SGST %', 'sgst': 'SGST %',
  'cgst %': 'CGST %', 'cgst': 'CGST %',
  'igst %': 'IGST %', 'igst': 'IGST %',
  'cess %': 'Cess %', 'cess': 'Cess %',
  'opening stock': 'Opening Stock', 'op': 'Opening Stock', 'openingstock': 'Opening Stock',
  'location': 'Location', 'location name': 'Location',
  'isservice': 'isService', 'is service': 'isService',
  'istrackable': 'isTrackable', 'is trackable': 'isTrackable',
  'discount %': 'Discount %', 'discount': 'Discount %',
  'hasserialnosstr': 'hasSerialNosStr', 'has serial': 'hasSerialNosStr', 'hasserial': 'hasSerialNosStr',
};

interface Product {
  Code: number;
  Title: string;
  Product: string;
  Barcode: string;
  CreatedOn?: string;
  HSN?: string;
  warranty?: number;
  Addedby?: string;
  MRP: number;
  CostPrice?: number;
  CategoryID?: number;
  UQCName?: string;
  DiscountPer?: string;
  Discount?: number;
  BrandName?: string;
  Color?: string;
  Style?: string;
  Stylecode?: string;
  Size?: string;
  NetQty?: string;
  Mfd?: string;
  Extra?: string;
  Articlecode?: string;
  MinStQty?: string;
  SgstPer?: number;
  CGstPer?: number;
  IgstPer?: number;
  Available: number;
  Sold?: number;
  isTraceable?: boolean;
  isService?: boolean;
  hasSerial?: number;
  CessPer?: number;
  Category: string;
  CollectionName?: string;
  CollectionID?: number;
  imgStr: string | null;
  SPExclGST?: number;
  BasicPrice?: number;
  LocationID?: number;
}

interface InventorySectionProps {
  token: string;
  onBack: () => void;
}

// Helper functions (unchanged)
const getImageUrl = (imgStr: string | null): string | null => {
  if (!imgStr) return null;
  if (imgStr.startsWith('http://') || imgStr.startsWith('https://')) {
    return imgStr;
  }
  const baseUrl = getBaseUrl();
  return `${baseUrl}/${imgStr.replace(/^\/+/, '')}`;
};

const getFallbackImage = (productName: string): string => {
  const encodedName = encodeURIComponent(productName.substring(0, 8));
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg">
      <rect width="150" height="150" fill="#f3f4f6"/>
      <text x="75" y="75" font-family="Arial, sans-serif" font-size="12" fill="#6b7280" text-anchor="middle" dy=".3em">${encodedName}</text>
    </svg>
  `)}`;
};

const getIconFallback = (): string => {
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg">
      <rect width="150" height="150" fill="#f3f4f6"/>
      <rect x="50" y="50" width="50" height="50" fill="#d1d5db" rx="4"/>
      <rect x="60" y="60" width="30" height="30" fill="#9ca3af" rx="2"/>
    </svg>
  `)}`;
};

function formatDateDMY(dateStr?: string) {
  if (!dateStr) return 'N/A';
  const match = dateStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (!match) return dateStr;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
    <div className="h-12 w-full bg-gray-200 rounded animate-pulse" />
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 w-full bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
  </div>
);

const ProductCard = ({ product, onViewDetails, onEdit, onDelete, onUploadImage, onUpdateQuantity, imgInInventory, canEdit, canDelete, canUpdateQuantity }: { product: Product; onViewDetails: (product: Product) => void; onEdit: (product: Product) => void; onDelete: (product: Product) => void; onUploadImage: (product: Product) => void; onUpdateQuantity: (product: Product) => void; imgInInventory: boolean; canEdit?: boolean; canDelete?: boolean; canUpdateQuantity?: boolean }) => {
  const isAndroid = useIsAndroid();
  return (
    <Card className="bg-white shadow-sm border-gray-100 h-full overflow-hidden">
      <div className={`p-4 ${isAndroid ? 'pt-6' : ''}`}>
        {/* Android: Title at top-left, Desktop: Original layout */}
        {isAndroid && (
          <h3 className="text-gray-900 font-semibold text-left w-full mb-3 break-words leading-tight min-h-0">{product.Title}</h3>
        )}
        
        <div className="flex flex-row items-start gap-4">
          {imgInInventory && (
          <div className={`w-28 h-28 sm:w-36 sm:h-36 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center shrink-0 ${isAndroid ? 'mt-2' : ''}`}>
            {product.imgStr ? (
              <img
                src={getImageUrl(product.imgStr)}
                alt={product.Title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const failedUrl = e.currentTarget.src;
                  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
                    console.log('❌ Section image failed to load:', failedUrl, 'for product:', product.Title);
                  }
                  if (!failedUrl.includes('data:image/svg+xml')) {
                    e.currentTarget.src = getFallbackImage(product.Title);
                  }
                }}
                onLoad={() => {
                  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
                    console.log('✅ Section image loaded successfully for:', product.Title);
                  }
                }}
              />
            ) : (
              <Package className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400" />
            )}
          </div>
          )}
          <div className="flex-1 space-y-3 min-w-0">
            {/* Desktop: Title in original position */}
            {!isAndroid && (
              <h3 className={`text-gray-900 font-semibold line-clamp-2 truncate ${imgInInventory ? 'max-w-[120px] sm:max-w-none' : ''}`}>{product.Title}</h3>
            )}
            <div className={`space-y-2 ${isAndroid ? 'pt-2' : ''}`}>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-gray-500 text-sm">Barcode:</span>
                <div className="w-full">
                  <div className="bg-gray-50 border border-gray-200 rounded-full text-xs font-semibold cursor-pointer w-full max-w-full barcode-container">
                    <span className="barcode-text">{product.Barcode}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Stock:</span>
                <Badge
                  variant={product.Available > 0 ? "success" : "destructive"}
                  className={
                    product.Available > 0
                      ? "bg-green-50 text-green-700 border-0"
                      : "bg-red-50 text-red-700 border-0"
                  }
                >
                  {product.Available} units
                </Badge>
              </div>
              <div className="text-gray-900 font-semibold">
                ₹{product.MRP.toFixed(2)}
              </div>
              {/* Desktop/tablet actions inside details column */}
              <div className="hidden lg:flex gap-2 pt-2 flex-nowrap overflow-x-auto">
                {canEdit !== false && <Button size="icon" variant="ghost" className="text-blue-600 hover:bg-blue-100" onClick={() => onEdit(product)}><Edit className="w-5 h-5" /></Button>}
                {canDelete !== false && <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-100" onClick={() => onDelete(product)}><Trash2 className="w-5 h-5" /></Button>}
                <Button size="icon" variant="ghost" className="text-green-600 hover:bg-green-100" onClick={e => { e.stopPropagation(); onUploadImage(product); }} title="Upload Image">
                  <ImageIcon className="w-5 h-5" />
                </Button>
                {canUpdateQuantity !== false && <Button size="icon" variant="ghost" className="text-purple-600 hover:bg-purple-100" onClick={e => { e.stopPropagation(); onUpdateQuantity(product); }} title="Update Quantity"><PackageCheck className="w-5 h-5" /></Button>}
              </div>
              <Button
                variant="link"
                className="hidden lg:inline text-blue-500 hover:text-blue-600 p-0"
                onClick={() => onViewDetails(product)}
              >
                More
              </Button>
            </div>
          </div>
        </div>
        {/* Mobile actions strip spanning full card width under content */}
        <div className="mt-2 flex lg:hidden w-full">
          <div className="w-full px-2 py-2 rounded-md border border-transparent flex items-center gap-4 flex-nowrap">
            {canEdit !== false && <Button size="icon" variant="ghost" className="text-blue-600 hover:bg-blue-100" onClick={() => onEdit(product)}><Edit className="w-5 h-5" /></Button>}
            {canDelete !== false && <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-100" onClick={() => onDelete(product)}><Trash2 className="w-5 h-5" /></Button>}
            <Button size="icon" variant="ghost" className="text-green-600 hover:bg-green-100" onClick={e => { e.stopPropagation(); onUploadImage(product); }} title="Upload Image">
              <ImageIcon className="w-5 h-5" />
            </Button>
            {canUpdateQuantity !== false && <Button size="icon" variant="ghost" className="text-purple-600 hover:bg-purple-100" onClick={e => { e.stopPropagation(); onUpdateQuantity(product); }} title="Update Quantity"><PackageCheck className="w-5 h-5" /></Button>}
            <button
              className="text-blue-500 hover:text-blue-600 text-sm whitespace-nowrap"
              onClick={() => onViewDetails(product)}
            >
              More
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const PRODUCT_COLUMNS = [
  { key: 'Title', label: 'Product' },
  { key: 'Barcode', label: 'Barcode' },
  { key: 'MRP', label: 'Sp incl GST (MRP)' },
  { key: 'SPExclGST', label: 'Sp excl GST (MRP)' },
  { key: 'CostPrice', label: 'Cost Price' },
  { key: 'Available', label: 'Available' },
  { key: 'Sold', label: 'Sold' },
  { key: 'Category', label: 'Category' },
  { key: 'BrandName', label: 'Brand Name' },
  { key: 'DiscountPer', label: 'Discount%' },
  { key: 'Discount', label: 'Discount' },
  { key: 'BasicPrice', label: 'Base Price' },
  { key: 'Mfd', label: 'MFD' },
  { key: 'Size', label: 'Size' },
  { key: 'Style', label: 'Shelf' },
  { key: 'Stylecode', label: 'Rack' },
  { key: 'MinStQty', label: 'ROL' },
  { key: 'SgstPer', label: 'SGST%' },
  { key: 'CGstPer', label: 'CGST%' },
  { key: 'IgstPer', label: 'IGST%' },
  { key: 'CessPer', label: 'CESS%' },
  { key: 'isTraceable', label: 'isTrackable' },
  { key: 'hasSerial', label: 'HasSerial' },
  { key: 'isService', label: 'Service' },
];

const computeOfferPrice = (p: Product | null | undefined): number => {
  if (!p) return 0;
  const mrpNum = Number(p.MRP) || 0;
  const spExclGSTNum = typeof p.SPExclGST === 'number' ? p.SPExclGST : 0;
  const sgstValue = Number(p.SgstPer || 0);
  const cgstValue = Number(p.CGstPer || 0);
  const cessValue = Number(p.CessPer || 0);
  const discountPercent = Number(p.DiscountPer || 0);
  const discountAmount = Number(p.Discount || 0);
  const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

  if (discountPercent > 0) {
    const spExclGST = spExclGSTNum || (mrpNum / (1 + totalTaxRate));
    const discountedPrice = spExclGST * (1 - discountPercent / 100);
    return Number((discountedPrice * (1 + totalTaxRate)).toFixed(2));
  }
  if (discountAmount > 0) {
    return Number((mrpNum - discountAmount).toFixed(2));
  }
  return Number(mrpNum.toFixed(2));
};

const InventorySection = ({ token, onBack }: InventorySectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedProductCodes, setSelectedProductCodes] = useState<number[]>([]);
  const [selectedProductCode, setSelectedProductCode] = useState<number | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [showSelectProductSplash, setShowSelectProductSplash] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [locations, setLocations] = useState<{ ID: string; Name: string }[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('All');
  const [showUpdateQuantityModal, setShowUpdateQuantityModal] = useState(false);
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
  const [selectedProductForSerial, setSelectedProductForSerial] = useState<Product | null>(null);
  const [addProductSettings, setAddProductSettings] = useState<{ id: number; Name: string; Value: string }[] | null>(null);
  const [showLocationManagement, setShowLocationManagement] = useState(false);
  // Excel import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProgress, setImportProgress] = useState({ inserted: 0, total: 0 });
  const [importPhase, setImportPhase] = useState<'idle' | 'parsing' | 'importing' | 'done'>('idle');
  const [importResult, setImportResult] = useState<{
    successCount: number;
    failedRows: { row: number; productName: string; reason: string }[];
    duplicateBarcodes: { barcode: string; rows: { row: number; productName: string }[] }[];
    skippedRows: { row: number; productName: string; reason: string }[];
    reportBuf?: ArrayBuffer;
  } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [newQuantity, setNewQuantity] = useState<number>(0);
  // String state dedicated to the Update Quantity input so users can type "-" or partial numbers
  const [newQuantityInput, setNewQuantityInput] = useState<string>('0');
  const [selectedLocationForUpdate, setSelectedLocationForUpdate] = useState<string>('');
  const [currentQuantity, setCurrentQuantity] = useState<number>(0);
  const [isLoadingQuantity, setIsLoadingQuantity] = useState(false);
  const [isTableScrollable, setIsTableScrollable] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Lazy loading state for mobile
  const [mobileLoadedCount, setMobileLoadedCount] = useState(20); // Initial load count
  const [isMobileLoading, setIsMobileLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isDesktopView, setIsDesktopView] = useState(window.innerWidth >= 1024);
  
  // Refs
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const productFormRef = useRef<ProductEntryFormHandle>(null);
  const barcodeExistsCache = useRef<Map<string, boolean>>(new Map());
  const categoryIdCache = useRef<Map<string, number>>(new Map());

  // Hooks
  const { salesInvoiceSettings, getSetting } = useSettings();
  const locationDisplayName = (getSetting('Location Name') || '').trim() || 'Locations';
  const imgInInventory = salesInvoiceSettings?.imgInInventory ?? false;
  const isAndroid = useIsAndroid();
  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPrivilege } = useUserPrivileges();
  const canProductCreate = hasPrivilege(PRIVILEGE.PRODUCT_CREATE);
  const canViewProduct = hasPrivilege(PRIVILEGE.PRODUCT_VIEW);
  const canEditProduct = hasPrivilege(PRIVILEGE.PRODUCT_UPDATE);
  const canDeleteProduct = hasPrivilege(PRIVILEGE.PRODUCT_DELETE);
  const canUpdateQuantityProduct = hasPrivilege(PRIVILEGE.QUANTITY_UPDATE);
  const canAddLocations = hasPrivilege(PRIVILEGE.ADD_LOCATIONS);
  const showAllBranches = hasPrivilege(PRIVILEGE.ALL_BRANCHES);

  // Custom back handler: close form/modal first, then go back
  const handleInventoryBack = useCallback(() => {
    if (showAddProductModal) {
      setShowAddProductModal(false);
      return;
    }
    if (showEditProductModal) {
      setShowEditProductModal(false);
      return;
    }
    if (showDeleteDialog) {
      setShowDeleteDialog(false);
      return;
    }
    if (showUpdateQuantityModal) {
      setShowUpdateQuantityModal(false);
      return;
    }
    if (showImportModal) {
      setShowImportModal(false);
      return;
    }
    if (selectedProduct) {
      setSelectedProduct(null);
      return;
    }
    onBack?.();
  }, [onBack, showAddProductModal, showEditProductModal, showDeleteDialog, showUpdateQuantityModal, showImportModal, selectedProduct]);

  useBackNavigation({
    onBack: handleInventoryBack,
    canGoBack: true,
    showExitConfirmation: false
  });

  // Fetch settings on mount (for table column visibility) and when Add New Product dialog is opened
  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/settings`, {
          headers: {
            Authorization: authToken,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (cancelled) return;
        if (!response.ok) throw new Error('Failed to fetch settings');
        const json = await response.json();
        if (json?.data != null) setAddProductSettings(json.data);
      } catch (e) {
        if (!cancelled) console.error('Error fetching settings:', e);
      }
    };
    fetchSettings();
    return () => { cancelled = true; };
  }, [baseUrl, authToken]);

  // Visibility of Service / Trackable checkboxes for Add New Product dialog.
  // Service checkbox should always be available, regardless of settings.
  const showServiceCheckbox = true;
  const showTrackableCheckbox = (() => {
    if (!addProductSettings) return false;
    const item = addProductSettings.find((s) => s.Name === 'Products - isTrackable Ticked');
    return item?.Value === '1';
  })();
  const expiryForeverDefault = (() => {
    if (!addProductSettings) return true;
    const item = addProductSettings.find((s) => s.Name === 'Expiry forever');
    return item?.Value === '1';
  })();

  const { data: apiResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['products', token, selectedLocation],
    enabled: canViewProduct,
    queryFn: async () => {
      const locationParam = selectedLocation && selectedLocation !== 'All' ? selectedLocation : '';
      const batchSize = 200;
      let allProducts: any[] = [];
      let offset = 0;
      let hasMoreData = true;
      let batchNumber = 1;

      console.log(`Starting to fetch products for location: ${locationParam || 'All'}`);

      // Fetch products in batches until all records are retrieved
      while (hasMoreData) {
        console.log(`Fetching batch ${batchNumber} - offset: ${offset}, maxRecords: ${batchSize}`);
        
        const response = await fetch(
          `${baseUrl}/api/Products?PageStart=${offset}&MaxRecords=${batchSize}&LocationID=${locationParam}&CategoryID=all&FilterValue=`,
          {
            headers: {
              'Authorization': authToken,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        
        console.log(`Batch ${batchNumber} response:`, {
          receivedCount: data.data?.length || 0,
          totalSoFar: allProducts.length + (data.data?.length || 0),
          hasData: !!data.data,
          isArray: Array.isArray(data.data)
        });
        
        // Add the fetched products to our collection
        if (data.data && Array.isArray(data.data) && data.data.length > 0) {
          allProducts = [...allProducts, ...data.data];
          
          // If we received fewer records than requested, we've reached the end
          if (data.data.length < batchSize) {
            console.log(`Reached end: received ${data.data.length} records (less than batch size ${batchSize})`);
            hasMoreData = false;
          } else {
            // Move to the next batch
            offset += batchSize;
            batchNumber++;
          }
        } else {
          // No more data available
          console.log(`No more data available in batch ${batchNumber}`);
          hasMoreData = false;
        }
      }

      // Deduplicate products based on Code (product ID) to ensure no duplicates
      const uniqueProducts = Array.from(
        new Map(allProducts.map(product => [product.Code, product])).values()
      );

      console.log(`=== FETCH COMPLETE ===`);
      console.log(`Total products fetched: ${allProducts.length}`);
      console.log(`Unique products after deduplication: ${uniqueProducts.length}`);
      console.log(`Duplicates removed: ${allProducts.length - uniqueProducts.length}`);
      console.log('First 3 products:', uniqueProducts.slice(0, 3));
      console.log('Last 3 products:', uniqueProducts.slice(-3));
      
      // Return data in the same format as the original API response
      return {
        data: uniqueProducts,
        success: true
      };
    }
  });

  // Initialize with more items on Android for better initial experience
  useEffect(() => {
    if (isAndroid) {
      setMobileLoadedCount(30); // Load more items initially on Android
    }
  }, [isAndroid]);

  // Track window resize to update desktop/mobile view
  useEffect(() => {
    const handleResize = () => {
      const newIsDesktopView = window.innerWidth >= 1024;
      if (newIsDesktopView !== isDesktopView) {
        setIsDesktopView(newIsDesktopView);
        console.log('📱 View mode changed:', newIsDesktopView ? 'Desktop' : 'Mobile');
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDesktopView]);

  // Reset to first page when filtered products count changes or when pageSize changes
  useEffect(() => {
    if (currentPage > Math.ceil((apiResponse?.data?.length || 0) / pageSize)) {
      setCurrentPage(1);
      console.log('⚠️ Current page reset to 1 due to data or page size change');
    }
  }, [apiResponse?.data?.length, pageSize, currentPage]);

  // This effect manages scroll event listeners.
  useEffect(() => {
    if (!isLoading && apiResponse?.data) {
      const container = scrollContainerRef.current;
      if (container) {
        const updateScrollState = () => handleScroll();
        container.addEventListener('scroll', updateScrollState);
        window.addEventListener('resize', updateScrollState);
        
        // Run the check once initially
        updateScrollState();

        return () => {
          if (container) {
            container.removeEventListener('scroll', updateScrollState);
          }
          window.removeEventListener('resize', updateScrollState);
        };
      }
    }
  }, [isLoading, apiResponse]);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/Locations`, {
          headers: { Authorization: authToken },
        });
        const data = await response.json();
        if (data.data) setLocations(data.data);
      } catch (err) {
        setLocations([]);
      }
    };
    fetchLocations();
  }, [baseUrl, authToken]);

  // Reset current page when location changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLocation]);

  useEffect(() => {
    // Clear any selected products when location changes
    setSelectedProductCodes([]);
    setSelectedProductCode(null);
    setSelectedProductName(null);
  }, [selectedLocation]);

  // When user does not have "All Branches" privilege, default location to first option instead of "All"
  useEffect(() => {
    if (showAllBranches) return;
    if (selectedLocation === 'All' && locations.length > 0) {
      setSelectedLocation(locations[0].ID);
    }
  }, [showAllBranches, selectedLocation, locations]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      setSelectedProductCode(null);
      setSelectedProductName(null);
      setSelectedProductCodes([]);
    };
  }, []);



  // Reset lazy loading when search or location changes
  useEffect(() => {
    setMobileLoadedCount(20);
    setHasMoreData(true);
    setIsMobileLoading(false);
  }, [searchQuery, selectedLocation]);

  // Handle window resize for orientation changes on Android
  useEffect(() => {
    const handleResize = () => {
      // Reset lazy loading when screen size changes (orientation change)
      if (window.innerWidth < 1024) {
        setMobileLoadedCount(20);
        setHasMoreData(true);
        setIsMobileLoading(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pull-to-refresh functionality for Android
  useEffect(() => {
    if (!isAndroid || window.innerWidth >= 1024) return;

    let startY = 0;
    let currentY = 0;
    let isPulling = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        isPulling = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;
      
      currentY = e.touches[0].clientY;
      const pullDistance = currentY - startY;
      
      // If pulled down more than 100px, trigger refresh
      if (pullDistance > 100) {
        isPulling = false;
        // Refresh the data
        refetch();
        setMobileLoadedCount(20);
        setHasMoreData(true);
        setIsMobileLoading(false);
      }
    };

    const handleTouchEnd = () => {
      isPulling = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isAndroid, refetch]);

  // Update lazy loading state when filtered products change
  useEffect(() => {
    if (window.innerWidth < 1024) { // Only on mobile
      // This will be called after filteredProducts is defined
      // We'll use a ref or state to track the filtered length
      const currentFilteredLength = apiResponse?.data?.length || 0;
      
      // If we have more products than currently loaded, update hasMoreData
      if (mobileLoadedCount < currentFilteredLength) {
        setHasMoreData(true);
      } else if (mobileLoadedCount >= currentFilteredLength && currentFilteredLength > 0) {
        setHasMoreData(false);
      }
      
      console.log('🔄 Products updated:', {
        currentFilteredLength,
        mobileLoadedCount,
        hasMoreData: mobileLoadedCount < currentFilteredLength
      });
    }
  }, [mobileLoadedCount, apiResponse?.data?.length]);

  // Scroll functionality
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setScrollPosition(scrollLeft);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      console.log('Scroll state:', { scrollLeft, scrollWidth, clientWidth, canScrollLeft: scrollLeft > 0, canScrollRight: scrollLeft < scrollWidth - clientWidth - 1 });
    }
  };

  const scrollLeft = () => {
    console.log('Scroll left button clicked');
    if (scrollContainerRef.current) {
      console.log('Scrolling left, container found');
      try {
        scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
        // Fallback for older browsers
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft -= 300;
          }
        }, 100);
      } catch (error) {
        console.error('Error scrolling left:', error);
        // Direct scroll as fallback
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft -= 300;
        }
      }
    } else {
      console.error('Scroll container not found');
    }
  };

  const scrollRight = () => {
    console.log('Scroll right button clicked');
    if (scrollContainerRef.current) {
      console.log('Scrolling right, container found');
      try {
        scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        // Fallback for older browsers
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft += 300;
          }
        }, 100);
      } catch (error) {
        console.error('Error scrolling right:', error);
        // Direct scroll as fallback
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft += 300;
        }
      }
    } else {
      console.error('Scroll container not found');
    }
  };



  const fetchCurrentQuantity = async (productCode: number, locationId: string) => {
    setIsLoadingQuantity(true);
    try {
      const response = await fetch(
        `${baseUrl}/api/LocatioWiseProductAvailability?ProductCode=${productCode}`,
        {
          headers: {
            'Authorization': authToken,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        setCurrentQuantity(0);
        return;
      }
      
      const data = await response.json();
      if (data.status === 0 && data.data) {
        const locationData = data.data.find((item: any) => item.LocationID.toString() === locationId);
        setCurrentQuantity(locationData ? locationData.Avl : 0);
      } else {
        setCurrentQuantity(0);
      }
    } catch (error) {
      console.error('Error fetching current quantity:', error);
      setCurrentQuantity(0);
    } finally {
      setIsLoadingQuantity(false);
    }
  };

  const updateProductQuantity = async () => {
    if (!selectedProductCode || !selectedLocationForUpdate) {
      toast({
        title: 'Error',
        description: 'Please provide valid product and quantity information.',
        variant: 'destructive'
      });
      return;
    }

    setIsUpdatingQuantity(true);
    try {
      // Parse the input string into a number, accept negative values
      const parsedQty = Number(newQuantityInput.trim() === '' || newQuantityInput.trim() === '-' ? 0 : newQuantityInput);
      setNewQuantity(parsedQty);
      const payload = {
        ProductCode: selectedProductCode,
        LocationID: parseInt(selectedLocationForUpdate),
        Qty: parsedQty
      };
      
      console.log('[UPDATE_QUANTITY] API Request Payload:\n' + JSON.stringify(payload, null, 2));
      
      const response = await fetch(`${baseUrl}/api/UpdateQty`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('Update Quantity API Response:\n' + JSON.stringify(result, null, 2));

      if (result.status === 0 || result.status === 1) {
        toast({
          title: 'Success',
          description: 'Quantity updated successfully.'
        });
        setShowUpdateQuantityModal(false);
        setNewQuantity(0);
        setNewQuantityInput('0');
        setSelectedLocationForUpdate('');
        setCurrentQuantity(0);
        setSelectedProductCodes([]);
        setSelectedProductCode(null);
        setSelectedProductName(null);
        // Refresh the products list
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to update quantity',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: 'Error',
        description: 'Failed to update quantity. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsUpdatingQuantity(false);
    }
  };

  const handleUpdateQuantity = () => {
    if (selectedProductCode === null) {
      setShowSelectProductSplash(true);
      setTimeout(() => setShowSelectProductSplash(false), 2500);
      return;
    }
    
    // Initialize input to 0 every time the modal opens
    setNewQuantity(0);
    setNewQuantityInput('0');
    setCurrentQuantity(0);
    setShowUpdateQuantityModal(true);
  };

  // Effect to handle initial location selection and quantity fetching
  useEffect(() => {
    if (showUpdateQuantityModal && selectedProductCode) {
      if (selectedLocation === 'All') {
        // When "All" is selected, set first location as default and fetch its quantity
        if (locations.length > 0) {
          const firstLocationId = locations[0].ID;
          setSelectedLocationForUpdate(firstLocationId);
          fetchCurrentQuantity(selectedProductCode, firstLocationId);
        }
      } else {
        // When specific location is selected, use that location and fetch its quantity
        setSelectedLocationForUpdate(selectedLocation);
        fetchCurrentQuantity(selectedProductCode, selectedLocation);
      }
    }
  }, [showUpdateQuantityModal, selectedProductCode, selectedLocation, locations]);



  // Lazy loading logic for mobile - using refs to avoid hook order issues
  const loadMoreItems = useCallback(() => {
    // Get current values from refs to avoid stale closures
    const currentLoading = isMobileLoading;
    const currentCount = mobileLoadedCount;
    // Use raw data length for now, will be updated after filteredProducts is defined
    const totalCount = apiResponse?.data?.length || 0;
    
    if (currentLoading || currentCount >= totalCount) return;
    
    console.log('🔄 Loading more items:', {
      current: currentCount,
      total: totalCount,
      isAndroid,
      loading: currentLoading
    });
    
    setIsMobileLoading(true);
    const delay = isAndroid ? 300 : 200; // Slightly longer delay for Android
    
    setTimeout(() => {
      const newCount = Math.min(currentCount + 10, totalCount);
      setMobileLoadedCount(newCount);
      // Note: hasMoreData will be updated by the useEffect when filteredProducts changes
      setIsMobileLoading(false);
      
      console.log('✅ Loaded more items:', {
        newCount,
        total: totalCount,
        hasMore: newCount < totalCount
      });
    }, delay);
  }, [isMobileLoading, mobileLoadedCount, apiResponse?.data?.length, isAndroid]);

  // Improved scroll handler with better detection for Android
  const handleMobileScroll = useCallback(() => {
    if (window.innerWidth >= 1024) return; // Desktop - no lazy loading
    if (searchQuery) return; // No lazy loading if searching
    
    const currentLoading = isMobileLoading;
    const currentCount = mobileLoadedCount;
    // Use raw data length for now, will be updated after filteredProducts is defined
    const totalCount = apiResponse?.data?.length || 0;
    
    if (currentLoading || currentCount >= totalCount) return;

    // Use window scroll for mobile
    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    
    // More aggressive loading threshold for Android (load earlier)
    const threshold = isAndroid ? 500 : 300; // Increased threshold for better detection
    
    // Check if we're near the bottom
    const remainingToBottom = scrollHeight - (scrollTop + clientHeight);
    
    // Log scroll position for debugging
    console.log('📱 Scroll check:', {
      scrollTop,
      clientHeight,
      scrollHeight,
      threshold,
      remainingToBottom,
      mobileLoadedCount: currentCount,
      totalProducts: totalCount,
      isAndroid,
      shouldLoad: remainingToBottom < threshold
    });
    
    if (remainingToBottom < threshold) {
      console.log('🔄 Mobile scroll trigger - Loading more items!');
      loadMoreItems();
    }
  }, [searchQuery, isMobileLoading, mobileLoadedCount, apiResponse?.data?.length, loadMoreItems, isAndroid]);

  // Throttled scroll handler for better performance
  const throttledScrollHandler = useCallback(() => {
    let ticking = false;
    
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleMobileScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
  }, [handleMobileScroll]);

  // Set up scroll listener with Android-specific optimizations
  useEffect(() => {
    if (window.innerWidth >= 1024) return; // Only on mobile
    
    console.log('🎯 Setting up scroll listeners for mobile');
    
    // For mobile, we need to listen to window scroll, not container scroll
    const handler = throttledScrollHandler();
    
    // Use passive listeners for better performance on Android
    const options = { passive: true };
    
    window.addEventListener('scroll', handler, options);
    
    // For Android, also listen to touch events for better scroll detection
    if (isAndroid) {
      document.addEventListener('touchmove', handler, options);
      document.addEventListener('touchend', handler, options);
      console.log('📱 Added Android-specific touch listeners');
    }
    
    // Test the scroll handler immediately
    setTimeout(() => {
      console.log('🧪 Testing scroll handler...');
      handleMobileScroll();
    }, 1000);
    
    return () => {
      window.removeEventListener('scroll', handler);
      if (isAndroid) {
        document.removeEventListener('touchmove', handler);
        document.removeEventListener('touchend', handler);
      }
    };
  }, [throttledScrollHandler, isAndroid, handleMobileScroll]);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
        <h3 className="text-lg font-semibold mb-2">Error Loading Products</h3>
        <p className="text-sm">{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
      </div>
    );
  }

  const filteredProducts = (apiResponse?.data ?? [])
    .map(product => {
      const sgstValue = Number(product.SgstPer || 0);
      const cgstValue = Number(product.CGstPer || 0);
      const cessValue = Number(product.CessPer || 0);
      const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

      return {
        ...product,
        // Calculate SPExclGST from MRP and tax fields (GST amounts treated as % of MRP)
        SPExclGST: product.MRP && product.MRP > 0
          ? Number((product.MRP * (1 - totalTaxRate)).toFixed(2))
          : 0,
      };
    })
    .filter(product => {
      // If no search query, show all products
      if (!searchQuery || searchQuery.trim() === '') {
        return true;
      }
      
      const searchLower = searchQuery.toLowerCase();
      // Add null/undefined checks to prevent filtering out products with missing fields
      return (
        (product.Title && product.Title.toLowerCase().includes(searchLower)) ||
        (product.Barcode && product.Barcode.toLowerCase().includes(searchLower)) ||
        (product.Category && product.Category.toLowerCase().includes(searchLower))
      );
    });



  // Pagination logic
  const totalProducts = filteredProducts.length;
  const totalPages = Math.ceil(totalProducts / pageSize);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Get products to display based on view mode (desktop pagination vs mobile lazy loading)
  const productsToDisplay = isDesktopView 
    ? paginatedProducts 
    : filteredProducts.slice(0, mobileLoadedCount);

  const isProductSelected = (code: number) => selectedProductCodes.includes(code);

  const toggleProductSelection = (product: Product) => {
    setSelectedProductCodes((prev) => {
      const exists = prev.includes(product.Code);
      const next = exists ? prev.filter((c) => c !== product.Code) : [...prev, product.Code];

      if (next.length > 0) {
        const firstCode = next[0];
        const firstProduct = filteredProducts.find((p) => p.Code === firstCode);
        setSelectedProductCode(firstCode);
        setSelectedProductName(firstProduct ? firstProduct.Title : null);
      } else {
        setSelectedProductCode(null);
        setSelectedProductName(null);
      }

      return next;
    });
  };

  const areAllCurrentPageSelected =
    paginatedProducts.length > 0 && paginatedProducts.every((p) => isProductSelected(p.Code));

  const hasSelection = selectedProductCodes.length > 0;

  // Debug logging to understand product counts
  console.log('📊 Product Display Debug:', {
    'Total from API': apiResponse?.data?.length || 0,
    'After filtering': filteredProducts.length,
    'Current page': currentPage,
    'Page size': pageSize,
    'Total pages': totalPages,
    'Paginated products': paginatedProducts.length,
    'Mobile loaded count': mobileLoadedCount,
    'Products to display': productsToDisplay.length,
    'Is desktop view': isDesktopView,
    'Search query': searchQuery
  });

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
  };

  const closeProductDetails = () => {
    setSelectedProduct(null);
  };

  const handleDeleteProduct = (product: Product) => {
    setProductToDelete(product);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${baseUrl}/api/Product`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify({
          Code: productToDelete.Code
        })
      });

      const result = await response.json();
      console.log('Delete API Response:', result);

      if (
        result.status === 1 ||
        (result.status === 0 && result.message && result.message.toLowerCase() === 'success')
      ) {
        toast({
          title: 'Success',
          description: 'Product deleted successfully'
        });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to delete product',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setProductToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setProductToDelete(null);
  };

  const downloadProductTemplate = () => {
    const wsData = [PRODUCT_IMPORT_HEADERS, []]; // header row + empty second row
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const noteSheet = XLSX.utils.aoa_to_sheet([
      ['Required fields (must not be empty during import):'],
      ['Product Name', 'SGST %', 'CGST %', 'Cess %', 'UQC', 'Category', 'Barcode', 'HSN', 'Opening Stock (OP)', 'ROL', 'Location'],
      [],
      ['Opening Stock rules:'],
      ['For products with hasSerialNosStr = yes/1/true: Opening Stock must be 0 or empty.'],
      ['For other products: Opening Stock is required and must be a number >= 0.'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.utils.book_append_sheet(wb, noteSheet, 'Instructions');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'product_import_template.xlsx');
  };

  // Export currently loaded inventory (filtered) to Excel. No API fetch; uses data already in table.
  const EXPORT_EXCEL_HEADERS = [
    'Product Name', 'Price Inc GST', 'Category', 'Barcode', 'CostPrice', 'UQC', 'HSN', 'Brand Name',
    'Color', 'Rack', 'Shelf', 'Size', 'Article Code', 'Basic Price', 'MFG Date', 'ROL',
    'SGST %', 'CGST %', 'IGST %', 'Cess %', 'Opening Stock', 'Location', 'isService', 'isTrackable',
    'Discount %', 'hasSerialNosStr'
  ] as const;

  const exportToExcel = () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      toast({ title: 'No data to export', description: 'No data available to export.', variant: 'destructive' });
      return;
    }
    const locationLabel = selectedLocation === 'All'
      ? 'All'
      : (locations.find(l => l.ID === selectedLocation)?.Name ?? selectedLocation);

    const rows = filteredProducts.map((p): (string | number)[] => {
      const mrp = Number(p.MRP) || 0;
      const costPrice = Number(p.CostPrice) || 0;
      const basicPrice = Number(p.BasicPrice) || 0;
      const available = Number(p.Available) || 0;
      const sgst = Number(p.SgstPer) ?? 0;
      const cgst = Number(p.CGstPer) ?? 0;
      const igst = Number(p.IgstPer) ?? 0;
      const cess = Number(p.CessPer) ?? 0;
      const discountPer = typeof p.DiscountPer === 'string' ? parseFloat(p.DiscountPer) || 0 : (Number(p.DiscountPer) || 0);
      const isService = p.isService === true || p.isService === 1 ? 1 : 0;
      const isTrackable = p.isTraceable === true || p.isTraceable === 1 ? 1 : 0;
      const hasSerial = p.hasSerial === 1 || p.hasSerial === true ? 1 : 0;
      const mfd = p.Mfd ? (typeof p.Mfd === 'string' ? p.Mfd : String(p.Mfd)) : '';

      return [
        p.Title ?? '',
        mrp,
        p.Category ?? p.CollectionName ?? '',
        p.Barcode ?? '',
        costPrice,
        p.UQCName ?? 'PIECES',
        p.HSN ?? '',
        p.BrandName ?? 'N/A',
        p.Color ?? 'N/A',
        p.Stylecode ?? '',
        p.Style ?? '',
        p.Size ?? 'N/A',
        p.Articlecode ?? '',
        basicPrice,
        mfd,
        p.MinStQty ?? '',
        sgst,
        cgst,
        igst,
        cess,
        available,
        locationLabel,
        isService,
        isTrackable,
        discountPer,
        hasSerial
      ];
    });

    const wsData = [EXPORT_EXCEL_HEADERS as unknown as (string | number)[], ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Transactions');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `Inventory_Transactions_${dateStr}.xlsx`;
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
    toast({ title: 'Export complete', description: `${filteredProducts.length} record(s) exported.` });
  };

  const EXPORT_PRODUCTS_HEADERS = [
    'Product',
    'Barcode',
    'Sp incl GST (MRP)',
    'Sp excl GST (MRP)',
    'Cost Price',
    'Offer Price',
    'Available',
    'Sold',
    'Category',
    'Brand Name',
    'UQC',
    'Discount%',
    'Discount',
    'Base Price',
    'MFD',
    'Size',
    'Shelf',
    'Rack',
    'ROL',
    'SGST%',
    'CGST%',
    'IGST%',
    'CESS%',
    'isTrackable',
    'HasSerial',
    'Service',
    'Actions',
  ] as const;

  const exportProducts = () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      toast({ title: 'No data to export', description: 'No data available to export.', variant: 'destructive' });
      return;
    }
    const locationLabel = selectedLocation === 'All'
      ? 'All'
      : (locations.find(l => l.ID === selectedLocation)?.Name ?? selectedLocation);

    const rows = filteredProducts.map((p): (string | number)[] => {
      const mrp = Number(p.MRP) || 0;
      const spExcl = Number(p.SPExclGST) || 0;
      const costPrice = Number(p.CostPrice) || 0;
      const offer = computeOfferPrice(p);
      const available = Number(p.Available) || 0;
      const sold = Number(p.Sold) || 0;
      const discountPer = typeof p.DiscountPer === 'string' ? parseFloat(p.DiscountPer) || 0 : (Number(p.DiscountPer) || 0);
      const discountAmt = Number(p.Discount) || 0;
      const basePrice = Number(p.BasicPrice) || 0;
      const mfd =
        p.Mfd != null
          ? (typeof p.Mfd === 'string'
              ? p.Mfd
              : String(p.Mfd))
          : '';
      const rol = p.MinStQty ?? '';
      const sgst = Number(p.SgstPer) || 0;
      const cgst = Number(p.CGstPer) || 0;
      const igst = Number(p.IgstPer) || 0;
      const cess = Number(p.CessPer) || 0;
      const isTrackable = p.isTraceable === true || p.isTraceable === 1 ? 1 : 0;
      const hasSerial = p.hasSerial === 1 || p.hasSerial === true ? 1 : 0;
      const isService = p.isService === true || p.isService === 1 ? 1 : 0;

      return [
        p.Title ?? '',
        p.Barcode ?? '',
        mrp,
        spExcl,
        costPrice,
        offer,
        available,
        sold,
        p.Category ?? p.CollectionName ?? '',
        p.BrandName ?? 'N/A',
        p.UQCName ?? 'PIECES',
        discountPer,
        discountAmt,
        basePrice,
        mfd,
        p.Size ?? 'N/A',
        p.Style ?? '',
        p.Stylecode ?? '',
        rol,
        sgst,
        cgst,
        igst,
        cess,
        isTrackable,
        hasSerial,
        isService,
        '', // Actions column is purely UI in the table
      ];
    });

    const wsData = [EXPORT_PRODUCTS_HEADERS as unknown as (string | number)[], ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Products');
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `Inventory_Products_${dateStr}.xlsx`;
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
    toast({ title: 'Export complete', description: `${filteredProducts.length} product(s) exported.` });
  };

  const normalizeHeader = (h: string): string => EXCEL_HEADER_MAP[(h || '').toString().trim().toLowerCase()] || (h || '').toString().trim();
  const isNumericOnly = (v: unknown): boolean => {
    const s = String(v ?? '').trim();
    if (!s) return false;
    // Allow optional leading minus and decimal point (float-safe)
    return /^-?\d+(\.\d+)?$/.test(s);
  };
  const getRowValue = (row: Record<string, unknown>, canonicalKey: string): string | number | undefined => {
    const v = row[canonicalKey];
    if (v === undefined || v === null) return undefined;
    if (typeof v === 'number') return v;
    const s = String(v).trim();
    return s === '' ? undefined : s;
  };
  const parseBool = (v: unknown): boolean => {
    if (v === undefined || v === null) return false;
    const s = String(v).trim().toLowerCase();
    return s === '1' || s === 'yes' || s === 'true' || s === 'y';
  };
  const parseZeroOne = (v: unknown): 0 | 1 | null => {
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v === 0) return 0;
      if (v === 1) return 1;
      return null;
    }
    const s = String(v).trim();
    if (s === '0') return 0;
    if (s === '1') return 1;
    return null;
  };
  const parseNum = (v: unknown): number => {
    if (v === undefined || v === null || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const parseNumStrict = (v: unknown): number | null => {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  /** Returns 0 for null/empty/whitespace; otherwise integer. Use for qty, ROL, etc. */
  const getIntValue = (v: unknown): number => {
    if (v === null || v === undefined || String(v).trim() === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? Math.floor(n) : 0;
  };
  /** Returns 0 for null/empty/whitespace; otherwise decimal. Use for basicPrice, etc. */
  const getDecimalValue = (v: unknown): number => {
    if (v === null || v === undefined || String(v).trim() === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const formatDateYMD = (v: unknown): string | null => {
    if (v === undefined || v === null || v === '') return null;
    let d: Date;
    if (typeof v === 'number' && Number.isFinite(v)) {
      // Excel serial date (days since 1899-12-30); 25569 = Excel serial for 1970-01-01
      d = new Date((v - 25569) * 86400000);
    } else {
      const s = String(v).trim();
      if (!s) return null;
      // Parse DD-MM-YYYY or DD/MM/YYYY (user input)
      const dm = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
      if (dm) {
        const [, day, month, year] = dm;
        d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      } else {
        d = new Date(s);
      }
    }
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const startImport = () => {
    console.log('[Inventory Import] Import Excel clicked');
    setImportResult(null);
    setImportPhase('idle');
    setImportProgress({ inserted: 0, total: 0 });
    importFileInputRef.current?.click();
  };

  function generateBarcode(length: number) {
    let barcode = "";
    while (barcode.length < length) {
      barcode += Math.floor(Math.random() * 10);
    }
    return barcode;
  }

  const fetchBarcodeLengthSetting = async (): Promise<number> => {
    const res = await fetch(`${baseUrl}/api/Settings`, {
      headers: { Authorization: authToken, Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to fetch settings (HTTP ${res.status})`);
    const json = await res.json();
    const list: { Name?: string; Key?: string; Value?: string }[] = json?.data ?? [];
    const item = list.find((s) => (s.Name || s.Key) === 'Products - Barcode Length');
    const raw = (item?.Value ?? '').toString().trim();
    const n = Number(raw);
    // Safe fallback if missing/invalid
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 8;
  };

  const doesBarcodeExist = async (barcode: string): Promise<boolean> => {
    const b = String(barcode || '').trim();
    if (!b) return false;
    const cached = barcodeExistsCache.current.get(b);
    if (cached !== undefined) return cached;
    try {
      const res = await fetch(
        `${baseUrl}/api/Products?PageStart=0&MaxRecords=50&LocationID=&CategoryID=all&FilterValue=${encodeURIComponent(b)}`,
        { headers: { Authorization: authToken, Accept: 'application/json' } }
      );
      if (!res.ok) {
        barcodeExistsCache.current.set(b, false);
        return false;
      }
      const json = await res.json();
      const products: any[] = json?.data ?? [];
      const exists = Array.isArray(products) && products.some((p) => String(p?.Barcode ?? '').trim() === b);
      barcodeExistsCache.current.set(b, exists);
      return exists;
    } catch {
      barcodeExistsCache.current.set(b, false);
      return false;
    }
  };

  const getUniqueGeneratedBarcode = async (length: number, localUsed: Set<string>): Promise<string> => {
    const safeLen = Math.max(1, Math.floor(length));
    for (let attempts = 0; attempts < 200; attempts++) {
      const candidate = generateBarcode(safeLen);
      if (candidate.length !== safeLen) continue;
      if (localUsed.has(candidate)) continue;
      const exists = await doesBarcodeExist(candidate);
      if (exists) continue;
      localUsed.add(candidate);
      return candidate;
    }
    throw new Error('Unable to generate a unique barcode');
  };

  const fetchAllCategories = async (): Promise<any[]> => {
    const res = await fetch(`${baseUrl}/api/Categories`, {
      headers: { Authorization: authToken, Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to fetch categories (HTTP ${res.status})`);
    const json = await res.json();
    return Array.isArray(json?.data) ? json.data : [];
  };
  const ensureCategoryId = async (categoryName: string): Promise<{ id: number; created: boolean }> => {
    const name = String(categoryName || '').trim();
    if (!name) throw new Error('Missing Category');
    const key = name.toLowerCase();
    const cached = categoryIdCache.current.get(key);
    if (cached != null) {
      console.log('[Inventory Import] Category cache hit', { name, id: cached });
      return { id: cached, created: false };
    }

    // Required flow: GET /api/Categories and match by category name column; if not found, POST to create
    console.log('[Inventory Import] GET /api/Categories (check existing)');
    const all = await fetchAllCategories();
    const existing = all.find((c: any) =>
      String(c?.Category ?? c?.CollectionName ?? c?.Name ?? '').trim().toLowerCase() === key
    );
    if (existing) {
      const id = Number(existing?.CollectionID ?? existing?.collectionID ?? existing?.collectionId ?? existing?.id ?? existing?.Id ?? existing?.ID);
      if (Number.isFinite(id) && id > 0) {
        console.log('[Inventory Import] Category found in list', { name, id });
        categoryIdCache.current.set(key, id);
        return { id, created: false };
      }
    }

    // Create category
    const createBody = { CollectionName: name };
    console.log('[Inventory Import] POST /api/Categories (create)', { body: createBody });
    const createRes = await fetch(`${baseUrl}/api/Categories`, {
      method: 'POST',
      headers: { Authorization: authToken, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(createBody),
    });
    const createJson = await createRes.json().catch(() => ({}));
    console.log('[Inventory Import] POST /api/Categories (response)', {
      ok: createRes.ok,
      status: createRes.status,
      response: createJson,
    });
    if (!createRes.ok || (createJson?.status != null && createJson.status !== 0 && createJson.status !== 1)) {
      throw new Error(createJson?.message || `Category creation failed (HTTP ${createRes.status})`);
    }
    const created = (Array.isArray(createJson?.data) && createJson.data.length > 0) ? createJson.data[0] : createJson?.data;
    const createdId = Number(created?.CollectionID ?? created?.collectionID ?? created?.collectionId ?? created?.id ?? created?.Id ?? created?.ID);
    if (!Number.isFinite(createdId) || createdId <= 0) throw new Error('Category creation failed');
    console.log('[Inventory Import] Category created', { name, id: createdId });
    categoryIdCache.current.set(key, createdId);
    return { id: createdId, created: true };
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.name.endsWith('.xlsx')) {
      toast({ title: 'Invalid file', description: 'Please select an .xlsx file.', variant: 'destructive' });
      return;
    }
    setShowImportModal(true);
    setImportPhase('parsing');
    setImportResult(null);

    try {
      const data = await new Promise<ArrayBuffer>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as ArrayBuffer);
        r.onerror = () => reject(new Error('Failed to read file'));
        r.readAsArrayBuffer(file);
      });
      const wb = XLSX.read(data, { type: 'array' });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' }) as unknown[][];
      // Trim trailing empty rows (prevents lots of fake "missing field" errors)
      const isRowEmpty = (r: unknown[] | undefined) =>
        !r || r.every((cell) => String(cell ?? '').trim() === '');
      let lastNonEmpty = jsonRows.length - 1;
      while (lastNonEmpty >= 1 && isRowEmpty(jsonRows[lastNonEmpty])) lastNonEmpty--;
      const trimmedJsonRows = jsonRows.slice(0, Math.max(2, lastNonEmpty + 1));
      if (jsonRows.length < 2) {
        setImportPhase('done');
        setImportResult({
          successCount: 0,
          failedRows: [],
          duplicateBarcodes: [],
          skippedRows: [{ row: 1, productName: '-', reason: 'No data rows in sheet.' }],
        });
        return;
      }
      const rawHeaders = (trimmedJsonRows[0] as unknown[]).map((h) => String(h ?? '')) as string[];
      const canonicalHeaders = rawHeaders.map(h => normalizeHeader(h));
      const rows: Record<string, unknown>[] = [];
      for (let i = 1; i < trimmedJsonRows.length; i++) {
        const rawRow = (trimmedJsonRows[i] as unknown[]) || [];
        const row: Record<string, unknown> = {};
        rawHeaders.forEach((h, j) => {
          const key = canonicalHeaders[j] || String(j);
          row[key] = rawRow[j];
        });
        rows.push(row);
      }

      const allLocationsRes = await fetch(`${baseUrl}/api/AllLocations`, {
        headers: { Authorization: authToken, Accept: 'application/json' },
      });
      const allLocationsData = await allLocationsRes.json();
      const locationList: { Id: number; Name: string }[] = allLocationsData?.data ?? [];
      const locationMap: Record<string, string> = {};
      locationList.forEach((loc: { Id?: number; ID?: number; Name: string }) => {
        const id = loc.Id ?? (loc as any).ID;
        if (id != null && loc.Name != null) locationMap[String(loc.Name).trim().toLowerCase()] = String(id);
      });

      const localUsedBarcodes = new Set<string>();
      const duplicateBarcodeMap: Record<string, { row: number; productName: string }[]> = {};
      const skippedRows: { row: number; productName: string; reason: string }[] = [];

      // Per-row, per-column error messages (canonical key -> message). Missing means OK.
      const rowErrors = new Map<number, Record<string, string>>();
      const importStatusByRow = new Map<number, 'Success' | 'Failed'>();

      const setErr = (excelRow: number, canonicalKey: string, msg: string) => {
        const existing = rowErrors.get(excelRow) || {};
        existing[canonicalKey] = msg;
        rowErrors.set(excelRow, existing);
      };

      const getBool01 = (excelRow: number, canonicalKey: string): 0 | 1 => {
        const v = getRowValue(rows[excelRow - 2], canonicalKey);
        const parsed = parseZeroOne(v);
        if (parsed === null) {
          setErr(excelRow, canonicalKey, 'Value must be 0 or 1');
          return 0;
        }
        return parsed;
      };

      const getNum = (excelRow: number, canonicalKey: string, opts?: { required?: boolean; fieldLabel?: string; allowEmpty?: boolean; defaultZero?: boolean; nonNegative?: boolean }) => {
        const raw = getRowValue(rows[excelRow - 2], canonicalKey);
        const s = String(raw ?? '').trim();
        const label = opts?.fieldLabel || canonicalKey;
        if (!s) {
          if (opts?.required) {
            setErr(excelRow, canonicalKey, `${label} cannot be empty`);
            return NaN;
          }
          return opts?.defaultZero ? 0 : NaN;
        }
        if (!isNumericOnly(s)) {
          setErr(excelRow, canonicalKey, `${label} should be numeric`);
          return opts?.defaultZero ? 0 : NaN;
        }
        const n = Number(s);
        if (opts?.nonNegative && n < 0) {
          setErr(excelRow, canonicalKey, `${label} must be 0 or positive`);
          return opts?.defaultZero ? 0 : NaN;
        }
        return n;
      };

      // Pre-fetch barcode length setting (used for auto-generation when barcode is empty)
      const barcodeLength = await fetchBarcodeLengthSetting();
      // Map from excelRowNum -> auto-generated barcode (only for rows with no barcode in Excel)
      const autoBarcodeByRow = new Map<number, string>();

      // Shared result arrays (populated during both validation and insert phases)
      const failedRows: { row: number; productName: string; reason: string }[] = [];
      const reportRows: { rowNumber: number; productName: string; status: 'Success' | 'Failed'; reason: string }[] = [];

      // We will validate all rows; insert only when mandatory fields have no errors.
      const rowsToInsert: { rowIndex: number; rawRow: Record<string, unknown> }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const excelRowNum = i + 2;
        const productName = String(getRowValue(row, 'Product Name') ?? '').trim();
        if (!productName) continue;

        // Mandatory: Product Name
        if (!productName) setErr(excelRowNum, 'Product Name', 'Product Name cannot be empty');

        // Mandatory: Category
        const categoryName = String(getRowValue(row, 'Category') ?? '').trim();
        if (!categoryName) setErr(excelRowNum, 'Category', 'Category cannot be empty');

        // Barcode: auto-generate if empty; validate numeric if provided
        const barcodeRaw = String(getRowValue(row, 'Barcode') ?? '').trim();
        if (!barcodeRaw) {
          // Auto-generate a unique barcode using the configured length
          try {
            const generated = await getUniqueGeneratedBarcode(barcodeLength, localUsedBarcodes);
            autoBarcodeByRow.set(excelRowNum, generated);
            if (!duplicateBarcodeMap[generated]) duplicateBarcodeMap[generated] = [];
            duplicateBarcodeMap[generated].push({ row: excelRowNum, productName: productName || '-' });
          } catch {
            setErr(excelRowNum, 'Barcode', 'Could not auto-generate unique barcode');
          }
        } else if (!isNumericOnly(barcodeRaw)) {
          setErr(excelRowNum, 'Barcode', 'Barcode should contain numeric values only');
        } else {
          // Track duplicates in file
          if (!duplicateBarcodeMap[barcodeRaw]) duplicateBarcodeMap[barcodeRaw] = [];
          duplicateBarcodeMap[barcodeRaw].push({ row: excelRowNum, productName: productName || '-' });
          if (localUsedBarcodes.has(barcodeRaw)) {
            setErr(excelRowNum, 'Barcode', 'Duplicate barcode');
          } else {
            localUsedBarcodes.add(barcodeRaw);
          }
        }

        // Mandatory: Price Inc GST numeric
        getNum(excelRowNum, 'Price Inc GST', { required: true, fieldLabel: 'Price Inc GST' });

        // Opening Stock: same logic as Discount
        // - Empty → 0 internally, no error
        // - Non-numeric (e.g. "idns") → error, row skipped
        // - Negative decimal → error, row skipped
        // - Valid decimal (including 0) → ok, maps to Available in payload
        const os = getRowValue(row, 'Opening Stock');
        const osStr = String(os ?? '').trim();
        if (osStr) {
          if (!isNumericOnly(osStr)) {
            setErr(excelRowNum, 'Opening Stock', 'Available must be numeric (decimal values only)');
          } else if (Number(osStr) < 0) {
            setErr(excelRowNum, 'Opening Stock', 'Available must be 0 or positive');
          }
        }

        // Optional numeric fields: default to 0 on invalid, but record error
        const numFields: Array<[string, string]> = [
          ['CostPrice', 'CostPrice'],
          ['Basic Price', 'Basic Price'],
          ['ROL', 'ROL'],
          ['SGST %', 'SGST %'],
          ['CGST %', 'CGST %'],
          ['IGST %', 'IGST %'],
          ['Cess %', 'Cess %'],
          ['Discount %', 'Discount %'],
        ];
        for (const [k, label] of numFields) {
          const rawVal = getRowValue(row, k);
          const s = String(rawVal ?? '').trim();
          if (!s) {
            // Basic Price rule: empty -> 0, no error
            if (k === 'Basic Price') continue;
          } else if (!isNumericOnly(s)) {
            setErr(excelRowNum, k, `${label} should be numeric`);
          }
        }

        // Boolean 0/1 validation (default to 0 internally)
        getBool01(excelRowNum, 'isService');
        getBool01(excelRowNum, 'isTrackable');
        // Template uses hasSerialNosStr; support both keys
        const hsKey = (row['hasSerialNumber'] != null) ? 'hasSerialNumber' : 'hasSerialNosStr';
        const hsVal = parseZeroOne(getRowValue(row, hsKey));
        if (hsVal === null) setErr(excelRowNum, hsKey, 'Value must be 0 or 1');

        // Determine if this row can be inserted (mandatory keys must have no errors)
        const errs = rowErrors.get(excelRowNum) || {};
        // Barcode is excluded: it's auto-generated when empty, so only its own error blocks
        const mandatoryKeys = ['Product Name', 'Category', 'Price Inc GST', 'Opening Stock'];
        const hasMandatoryError = mandatoryKeys.some((k) => errs[k]) || !!errs['Barcode'];
        if (hasMandatoryError) {
          importStatusByRow.set(excelRowNum, 'Failed');
          // Show the actual errors so users know exactly what to fix
          const allErrMessages = Object.values(errs).join(', ');
          failedRows.push({ row: excelRowNum, productName: productName || '-', reason: allErrMessages || 'Validation errors' });
          reportRows.push({ rowNumber: excelRowNum, productName: productName || '-', status: 'Failed', reason: allErrMessages || 'Validation errors' });
          continue;
        }

        // Stash for insert (do not modify original sheet values)
        const istNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const createdOn = `${istNow.getFullYear()}-${String(istNow.getMonth() + 1).padStart(2, '0')}-${String(istNow.getDate()).padStart(2, '0')}`;
        rowsToInsert.push({ rowIndex: excelRowNum, rawRow: { ...row, __createdOn: createdOn } });
      }

      const duplicateBarcodes = Object.entries(duplicateBarcodeMap)
        .filter(([, arr]) => arr.length > 1)
        .map(([barcode, rowsList]) => ({ barcode, rows: rowsList }));

      setImportProgress({ inserted: 0, total: rowsToInsert.length });
      setImportPhase('importing');
      let successCount = 0;

      for (let i = 0; i < rowsToInsert.length; i++) {
        const { rowIndex, rawRow } = rowsToInsert[i];
        const title = String(getRowValue(rawRow, 'Product Name') ?? '').trim();
        try {
          // Barcode: use auto-generated if Excel was empty, otherwise use Excel value.
          const barcode = autoBarcodeByRow.get(rowIndex) || String(getRowValue(rawRow, 'Barcode') ?? '').trim();
          const exists = await doesBarcodeExist(barcode);
          if (exists) {
            setErr(rowIndex, 'Barcode', 'Duplicate barcode');
            importStatusByRow.set(rowIndex, 'Failed');
            failedRows.push({ row: rowIndex, productName: String(title || '-'), reason: 'Duplicate barcode' });
            reportRows.push({ rowNumber: rowIndex, productName: String(title || '-'), status: 'Failed', reason: 'Duplicate barcode' });
            continue;
          }

          // Step 3: Ensure category exists / auto-create and use CategoryID
          const categoryName = String(getRowValue(rawRow, 'Category') ?? '').trim();
          let categoryId: number;
          let categoryCreated = false;
          try {
            const catResult = await ensureCategoryId(categoryName);
            categoryId = catResult.id;
            categoryCreated = catResult.created;
          } catch (catErr) {
            setErr(rowIndex, 'Category', 'Category creation failed');
            importStatusByRow.set(rowIndex, 'Failed');
            failedRows.push({ row: rowIndex, productName: String(title || '-'), reason: 'Category creation failed' });
            reportRows.push({ rowNumber: rowIndex, productName: String(title || '-'), status: 'Failed', reason: 'Category creation failed' });
            continue;
          }

          const priceIncGst = parseNum(getRowValue(rawRow, 'Price Inc GST'));
          const totalGst = parseNum(getRowValue(rawRow, 'SGST %')) + parseNum(getRowValue(rawRow, 'CGST %')) + parseNum(getRowValue(rawRow, 'Cess %'));
          const spExclGst = totalGst >= 0 ? priceIncGst / (1 + totalGst / 100) : priceIncGst;
          const hasSerial01 = parseZeroOne(getRowValue(rawRow, 'hasSerialNosStr')) ?? 0;
          const hasSerial = hasSerial01 === 1;
          const isService01 = parseZeroOne(getRowValue(rawRow, 'isService')) ?? 0;
          const isTraceable01 = parseZeroOne(getRowValue(rawRow, 'isTrackable')) ?? 0;

          const payload: Record<string, unknown> = {
            Title: title,
            SgstPer: parseNum(getRowValue(rawRow, 'SGST %')),
            CGstPer: parseNum(getRowValue(rawRow, 'CGST %')),
            IgstPer: parseNum(getRowValue(rawRow, 'IGST %')),
            CessPer: parseNum(getRowValue(rawRow, 'Cess %')),
            MRP: priceIncGst,
            SPExclGST: Number(spExclGst.toFixed(2)),
            CostPrice: getDecimalValue(getRowValue(rawRow, 'CostPrice')),
            // Base Price: take from Excel if present; otherwise 0. Do not calculate.
            BasicPrice: getDecimalValue(
              getRowValue(rawRow, 'Base Price') ?? getRowValue(rawRow, 'Basic Price')
            ),
            // Opening Stock maps to Available (NetQty); use decimal value, default 0 when empty
            NetQty: hasSerial ? 0 : Math.max(0, getDecimalValue(getRowValue(rawRow, 'Opening Stock'))),
            Available: hasSerial ? 0 : Math.max(0, getDecimalValue(getRowValue(rawRow, 'Opening Stock'))),
            UQCName: String(getRowValue(rawRow, 'UQC') ?? 'PIECES').trim(),
            Category: categoryName,
            CategoryID: categoryId,
            CollectionName: categoryName,
            CollectionID: categoryId,
            Barcode: barcode,
            DiscountPer: Number((rawRow as any).__discountPer ?? 0),
            Discount: Number((rawRow as any).__discountPer ?? 0),
            HSN: String(getRowValue(rawRow, 'HSN') ?? '1000').trim(),
            BrandName: (String(getRowValue(rawRow, 'Brand Name') ?? '').trim() || 'N/A'),
            Size: String(getRowValue(rawRow, 'Size') ?? '').trim() || null,
            Articlecode: String(getRowValue(rawRow, 'Article Code') ?? '').trim() || null,
            Color: (String(getRowValue(rawRow, 'Color') ?? '').trim() || 'N/A'),
            Style: String(getRowValue(rawRow, 'Shelf') ?? '').trim() || null,
            Stylecode: String(getRowValue(rawRow, 'Rack') ?? '').trim() || null,
            Extra: null,
            MinStQty: getIntValue(getRowValue(rawRow, 'ROL')),
            imgStr: null,
            isService: isService01 === 1,
            hasSerial,
            isTraceable: isTraceable01 === 1,
            PMargin: 0,
            Rack: String(getRowValue(rawRow, 'Rack') ?? '').trim() || null,
            Shelf: String(getRowValue(rawRow, 'Shelf') ?? '').trim() || null,
            LocationID: locationMap[String(getRowValue(rawRow, 'Location') ?? '').trim().toLowerCase()],
            HasSerialNo: hasSerial ? 1 : 0,
            CreatedOn: String((rawRow as any).__createdOn ?? ''),
            Mfd: formatDateYMD(getRowValue(rawRow, 'MFG Date')),
          };

          // Step 4: Insert product
          console.log('[Inventory Import] POST /api/Product', { row: rowIndex, Title: title, payload });
          const res = await fetch(`${baseUrl}/api/Product`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: authToken },
            body: JSON.stringify(payload),
          });
          let result = await res.json();
          console.log('[Inventory Import] POST /api/Product (response)', { row: rowIndex, ok: res.ok, status: res.status, result });
          let productInsertOk = res.ok && (result.status === 0 || result.status === 1);

          // Compatibility fallback:
          // some backends resolve category strictly during insert and may still return
          // "Did not find category" on first attempt. Re-check/create category and retry once.
          if (!productInsertOk) {
            const msg = String(result?.message ?? '').toLowerCase();
            if (msg.includes('did not find category') || msg.includes('category not found')) {
              const refreshed = await ensureCategoryId(categoryName);
              payload.CategoryID = refreshed.id;
              payload.CollectionID = refreshed.id;
              payload.CollectionName = categoryName;
              payload.Category = categoryName;
              console.log('[Inventory Import] Retry POST /api/Product after category refresh', { row: rowIndex, Category: categoryName, CategoryID: refreshed.id });
              const retryRes = await fetch(`${baseUrl}/api/Product`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: authToken },
                body: JSON.stringify(payload),
              });
              result = await retryRes.json();
              console.log('[Inventory Import] Retry POST /api/Product (response)', { row: rowIndex, ok: retryRes.ok, status: retryRes.status, result });
              productInsertOk = retryRes.ok && (result.status === 0 || result.status === 1);
            }
          }

          if (productInsertOk) {
            successCount++;
            importStatusByRow.set(rowIndex, 'Success');
            const productCode = result.data?.[0]?.Code;
            const openingStock = Number(payload.NetQty ?? 0);
            if (productCode && openingStock > 0 && payload.LocationID) {
              try {
                const qtyRes = await fetch(`${baseUrl}/api/UpdateQty`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: authToken },
                  body: JSON.stringify({
                    ProductCode: productCode,
                    LocationID: payload.LocationID,
                    Qty: openingStock,
                  }),
                });
                const qtyResult = await qtyRes.json();
                if (!qtyRes.ok || (qtyResult.status !== 0 && qtyResult.status !== 1 && qtyResult.message?.toLowerCase() !== 'success')) {
                  failedRows.push({
                    row: rowIndex,
                    productName: String(payload.Title || '-'),
                    reason: `Product created but Opening Stock update failed: ${qtyResult.message || `HTTP ${qtyRes.status}`}`,
                  });
                  successCount--;
                  reportRows.push({ rowNumber: rowIndex, productName: String(payload.Title || '-'), status: 'Failed', reason: `Product created but Opening Stock update failed: ${qtyResult.message || `HTTP ${qtyRes.status}`}` });
                }
              } catch (qtyErr) {
                failedRows.push({
                  row: rowIndex,
                  productName: String(payload.Title || '-'),
                  reason: `Product created but Opening Stock update failed: ${qtyErr instanceof Error ? qtyErr.message : 'Network error'}`,
                });
                successCount--;
                reportRows.push({ rowNumber: rowIndex, productName: String(payload.Title || '-'), status: 'Failed', reason: `Product created but Opening Stock update failed: ${qtyErr instanceof Error ? qtyErr.message : 'Network error'}` });
              }
            }
            if (!failedRows.some(fr => fr.row === rowIndex)) {
              const successReason = categoryCreated ? 'Imported; Category auto-created' : 'Imported';
              reportRows.push({ rowNumber: rowIndex, productName: String(payload.Title || '-'), status: 'Success', reason: successReason });
            }
          } else {
            const apiMessage = String(result?.message || `HTTP ${res.status}`);
            const normalizedMessage = apiMessage.toLowerCase().includes('barcode')
              ? 'Duplicate barcode'
              : apiMessage;
            failedRows.push({
              row: rowIndex,
              productName: String(title || '-'),
              reason: normalizedMessage,
            });
            reportRows.push({ rowNumber: rowIndex, productName: String(title || '-'), status: 'Failed', reason: normalizedMessage });
            importStatusByRow.set(rowIndex, 'Failed');
          }
        } catch (err) {
          failedRows.push({
            row: rowIndex,
            productName: String(title || '-'),
            reason: err instanceof Error ? err.message : 'Network error',
          });
          reportRows.push({ rowNumber: rowIndex, productName: String(title || '-'), status: 'Failed', reason: err instanceof Error ? err.message : 'Network error' });
          importStatusByRow.set(rowIndex, 'Failed');
        }
        setImportProgress(prev => ({ ...prev, inserted: successCount }));
      }

      // Error report: append 2 blank cols then 2 summary cols: "Status" and "Error"
      // "Error" contains ALL error messages for that row joined by ", " in a single cell.
      const anyFailed = failedRows.length > 0 || skippedRows.length > 0;
      let reportBuf: ArrayBuffer | undefined = undefined;

      if (anyFailed) {
        const ref = firstSheet['!ref'] || 'A1';
        const range = XLSX.utils.decode_range(ref);
        const lastOriginalCol = range.e.c;
        const statusCol = lastOriginalCol + 3; // +1 blank, +2 blank, +3 = Status
        const errorCol  = lastOriginalCol + 4; // +4 = Error

        // Blank gap headers
        firstSheet[XLSX.utils.encode_cell({ r: 0, c: lastOriginalCol + 1 })] = { t: 's', v: '' };
        firstSheet[XLSX.utils.encode_cell({ r: 0, c: lastOriginalCol + 2 })] = { t: 's', v: '' };
        // Summary headers
        firstSheet[XLSX.utils.encode_cell({ r: 0, c: statusCol })] = { t: 's', v: 'Status' };
        firstSheet[XLSX.utils.encode_cell({ r: 0, c: errorCol  })] = { t: 's', v: 'Error' };

        for (let i = 1; i < trimmedJsonRows.length; i++) {
          const excelRowNum = i + 1;
          const src = (trimmedJsonRows[i] as unknown[]) || [];
          const rowObj: Record<string, unknown> = {};
          rawHeaders.forEach((_, j) => {
            const key = canonicalHeaders[j] || String(j);
            rowObj[key] = src[j];
          });
          const productName = String(getRowValue(rowObj, 'Product Name') ?? '').trim();
          if (!productName) continue;

          const errs = rowErrors.get(excelRowNum) || {};
          // Collect errors from both validation phase and insert phase
          const allErrMsgs: string[] = Object.values(errs);
          // Also pull in any insert-time error from failedRows for this row
          const insertFail = failedRows.find(fr => fr.row === excelRowNum);
          if (insertFail && !allErrMsgs.includes(insertFail.reason)) {
            allErrMsgs.push(insertFail.reason);
          }

          const st: 'Success' | 'Failed' = importStatusByRow.get(excelRowNum) ||
            (allErrMsgs.length > 0 ? 'Failed' : 'Success');
          const isFailed = st === 'Failed';

          const statusCell: any = { t: 's', v: st };
          if (isFailed) statusCell.s = { font: { bold: true, color: { rgb: 'CC0000' } } };

          const errorText = allErrMsgs.join(', ');
          const errorCell: any = { t: 's', v: errorText };
          if (isFailed && errorText) errorCell.s = { font: { color: { rgb: 'CC0000' } } };

          firstSheet[XLSX.utils.encode_cell({ r: i, c: statusCol })] = statusCell;
          firstSheet[XLSX.utils.encode_cell({ r: i, c: errorCol  })] = errorCell;
        }

        // Expand sheet range to include the new columns
        firstSheet['!ref'] = XLSX.utils.encode_range({ s: range.s, e: { r: range.e.r, c: errorCol } });

        reportBuf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        saveAs(
          new Blob([reportBuf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          file.name
        );
      }

      setImportPhase('done');
      setImportResult({
        successCount,
        failedRows,
        duplicateBarcodes,
        skippedRows,
        reportBuf,
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (err) {
      setImportPhase('done');
      setImportResult({
        successCount: 0,
        failedRows: [],
        duplicateBarcodes: [],
        skippedRows: [{ row: 0, productName: '-', reason: err instanceof Error ? err.message : 'Failed to parse file' }],
      });
    }
  };

  const handleUpdateProduct = () => {
    console.log('handleUpdateProduct triggered');
    setIsUpdating(true);
    if (productFormRef.current) {
      productFormRef.current.submit();
      setIsUpdating(false); // Reset updating state after submission
    } else {
      console.error('productFormRef is not properly initialized');
      toast({ title: 'Error', description: 'Failed to update product. Please try again.', variant: 'destructive' });
      setIsUpdating(false);
    }
  };

  if (showLocationManagement) {
    return <LocationManagementScreen token={token} onBack={() => setShowLocationManagement(false)} />;
  }

  return (
    <div className="w-full android-form-container overflow-visible md:max-w-4xl lg:max-w-5xl xl:max-w-none 2xl:max-w-none md:mx-auto xl:mx-0">
      <input
        type="file"
        ref={importFileInputRef}
        accept=".xlsx"
        className="hidden"
        onChange={handleImportFile}
      />
      {/* Mobile App Bar */}
      <div className="lg:hidden">
        <AppBar title="Inventory" onBack={onBack} showBackButton={true} />
      </div>

      {/* Mobile tools below App Bar - Search when Product View (32), Add Product when Product Create (30) */}
      <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm px-4 py-2">
        {(canViewProduct || canProductCreate) && (
        <div className="flex flex-row items-center justify-between gap-2">
          {canViewProduct && (
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search inventory..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 px-3 pr-10 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            />
            {searchQuery && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          )}
          {canProductCreate && (
          <>
            <button
              onClick={() => {
                console.log('Add Product button clicked');
                setShowAddProductModal(true);
              }}
              aria-label="Add Product"
              className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-blue-600 text-white flex"
            >
              <Plus className="w-5 h-5" />
            </button>
          </>
          )}
          {canAddLocations && (
          <button
            onClick={() => setShowLocationManagement(true)}
            aria-label={locationDisplayName}
            className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-gray-600 text-white flex"
          >
            <MapPin className="w-5 h-5" />
          </button>
          )}
        </div>
        )}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <label htmlFor="locationDropdown" className="text-sm font-medium">Location:</label>
          <select
            id="locationDropdown"
            value={selectedLocation}
            onChange={e => setSelectedLocation(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {showAllBranches && <option value="All">All</option>}
            {locations.map(loc => (
              <option key={loc.ID} value={loc.ID}>{loc.Name}</option>
            ))}
          </select>
          {canProductCreate && (
            <>
              <button onClick={downloadProductTemplate} aria-label="Download Template" className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-slate-600 text-white flex">
                <FileDown className="w-5 h-5" />
              </button>
              <button onClick={startImport} aria-label="Import Excel" className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-emerald-600 text-white flex">
                <Upload className="w-5 h-5" />
              </button>
              <button onClick={exportToExcel} aria-label="Export to Excel" className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-slate-600 text-white flex">
                <FileDown className="w-5 h-5" />
              </button>
              <button onClick={exportProducts} aria-label="Export Products" className="shrink-0 items-center justify-center w-10 h-10 rounded-md bg-slate-600 text-white flex">
                <FileDown className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Desktop Header Section - Sticky and fixed */}
      <div className="hidden lg:block lg:sticky lg:top-0 lg:z-50 lg:bg-white lg:border-b lg:shadow-sm">
        <div className="flex items-center gap-2 mb-2 mt-1 px-3 py-0 lg:px-4 lg:py-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center px-2 py-1"
          >
            <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5" />
            <span className="ml-1 text-sm lg:text-base">Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold ml-2">Inventory</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-2 sm:px-4 md:px-6 lg:px-8 lg:space-y-2">
        {/* Desktop Search and Controls - Sticky below header */}
        <div className="hidden lg:block lg:sticky lg:top-[80px] lg:z-40 lg:bg-white lg:border-b lg:shadow-sm p-4 space-y-4">
          <div className="flex flex-row items-center justify-between gap-2 px-2 py-2">
            {canViewProduct && (
            <div className="relative flex-1 sm:w-1/2 md:w-1/2 lg:w-1/2">
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 sm:h-10 px-3 pr-10 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            )}
            {canProductCreate && (
            <>
              <Button
                onClick={() => {
                  console.log('Add Product button clicked');
                  setShowAddProductModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto text-sm lg:text-sm xl:text-base px-3 py-1.5"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </>
            )}
            {canAddLocations && (
            <Button
              variant="outline"
              onClick={() => setShowLocationManagement(true)}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto text-sm lg:text-sm xl:text-base px-3 py-1.5"
            >
              <MapPin className="w-4 h-4 mr-2" />
              {locationDisplayName}
            </Button>
            )}
            <Button
              onClick={() => {
                if (!hasSelection) return;
                const firstCode = selectedProductCodes[0];
                const firstProduct = filteredProducts.find(p => p.Code === firstCode);
                navigate(`/upload-image?code=${firstCode}`, {
                  state: { productCode: firstCode, productName: firstProduct?.Title || null, token },
                });
              }}
              className={`bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto text-sm lg:text-sm xl:text-base px-3 py-1.5 ${!hasSelection ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!hasSelection}
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Upload Image
            </Button>
            {canUpdateQuantityProduct && (
            <Button
              onClick={() => {
                if (!hasSelection) return;
                const firstCode = selectedProductCodes[0];
                const firstProduct = filteredProducts.find(p => p.Code === firstCode);
                
                // Check if product has serial numbers
                if (firstProduct && firstProduct.hasSerial === 1) {
                  setSelectedProductForSerial(firstProduct);
                  setShowSerialNumberModal(true);
                } else {
                  setSelectedProductCode(firstCode);
                  setSelectedProductName(firstProduct ? firstProduct.Title : null);
                  setShowUpdateQuantityModal(true);
                }
              }}
              className={`bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto text-sm lg:text-sm xl:text-base px-3 py-1.5 ${!hasSelection ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!hasSelection}
            >
              <PackageCheck className="w-4 h-4 mr-2" />
              Update Quantity
            </Button>
            )}
          </div>
{/* 
          {hasSelection && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-blue-800 font-medium">Selection Active</span>
                  <span className="text-blue-600 text-sm">
                    {selectedProductName 
                      ? `Selected: ${selectedProductName}` 
                      : 'Select a product using the checkboxes'
                    }
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedProductCodes([]);
                    setSelectedProductCode(null);
                    setSelectedProductName(null);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )} */}

          {/* Desktop: Location + page size + helper text on a single row */}
          <div className="hidden lg:flex items-center justify-between gap-4 mb-2 mt-1">
            <div className="flex items-center gap-2">
              <label htmlFor="locationDropdown" className="text-sm font-medium">Location:</label>
              <select
                id="locationDropdown"
                value={selectedLocation}
                onChange={e => setSelectedLocation(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              >
                {showAllBranches && <option value="All">All</option>}
                {locations.map(loc => (
                  <option key={loc.ID} value={loc.ID}>{loc.Name}</option>
                ))}
              </select>
              {canProductCreate && (
                <>
                  <Button variant="outline" onClick={downloadProductTemplate} className="border-gray-300 text-gray-700 hover:bg-gray-50 text-sm px-3 py-1.5">
                    <FileDown className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <Button variant="outline" onClick={startImport} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm px-3 py-1.5">
                    <Upload className="w-4 h-4 mr-2" />
                    Import Excel
                  </Button>
                  <Button variant="outline" onClick={exportToExcel} className="border-slate-600 text-slate-700 hover:bg-slate-50 text-sm px-3 py-1.5">
                    <FileDown className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                <Button variant="outline" onClick={exportProducts} className="border-slate-600 text-slate-700 hover:bg-slate-50 text-sm px-3 py-1.5">
                  <FileDown className="w-4 h-4 mr-2" />
                  Export Products
                </Button>
                </>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const newPageSize = Number(e.target.value);
                    setPageSize(newPageSize);
                    setCurrentPage(1); // Reset to first page when changing page size
                    console.log('📄 Page size changed to:', newPageSize);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                  <option value={999999}>All</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              {/* <div className="flex items-center gap-2 text-sm text-blue-600 whitespace-nowrap">
                <ChevronLeft className="h-4 w-4" />
                <span>Use scroll buttons or drag to view all columns</span>
                <ChevronRight className="h-4 w-4" />
              </div> */}
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="hidden lg:block rounded-md border overflow-hidden group">
            {/* Background gradient for scroll buttons */}
            <div className="absolute top-0 left-0 w-16 h-full bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-16 h-full bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
            
            {/* Scrollable Table Container */}
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-300px)]"
              onScroll={handleScroll}
            >
              <div className="min-w-max">
                <Table className="[&_td]:py-3 [&_th]:py-3">
                  <TableHeader className="sticky top-0 z-30 bg-white">
                    <TableRow>
                      <TableHead className="w-[50px] bg-white">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={areAllCurrentPageSelected}
                          onChange={() => {
                            setSelectedProductCodes(prev => {
                              if (areAllCurrentPageSelected) {
                                // Deselect all products on current page
                                const codesToRemove = new Set(paginatedProducts.map(p => p.Code));
                                const next = prev.filter(code => !codesToRemove.has(code));
                                if (next.length === 0) {
                                  setSelectedProductCode(null);
                                  setSelectedProductName(null);
                                } else {
                                  const firstCode = next[0];
                                  const firstProduct = filteredProducts.find(p => p.Code === firstCode);
                                  setSelectedProductCode(firstCode);
                                  setSelectedProductName(firstProduct ? firstProduct.Title : null);
                                }
                                return next;
                              } else {
                                // Select all products on current page
                                const pageCodes = paginatedProducts.map(p => p.Code);
                                const merged = Array.from(new Set([...prev, ...pageCodes]));
                                const firstCode = merged[0];
                                const firstProduct = filteredProducts.find(p => p.Code === firstCode);
                                setSelectedProductCode(firstCode);
                                setSelectedProductName(firstProduct ? firstProduct.Title : null);
                                return merged;
                              }
                            });
                          }}
                        />
                      </TableHead>
                      {imgInInventory && <TableHead className="w-[64px] sticky left-0 bg-white z-20">Image</TableHead>}
                      <TableHead className="bg-white">Product</TableHead>
                      <TableHead className="bg-white">Barcode</TableHead>
                      <TableHead className="bg-white">Sp incl GST (MRP)</TableHead>
                      <TableHead className="bg-white">Sp excl GST (MRP)</TableHead>
                      <TableHead className="bg-white">Cost Price</TableHead>
                      <TableHead className="bg-white">Offer Price</TableHead>
                      <TableHead className="bg-white">Available</TableHead>
                      <TableHead className="bg-white">Sold</TableHead>
                      <TableHead className="bg-white">Category</TableHead>
                      <TableHead className="bg-white">Brand Name</TableHead>
                      <TableHead className="bg-white">UQC</TableHead>
                      <TableHead className="bg-white">Discount%</TableHead>
                      <TableHead className="bg-white">Discount</TableHead>
                      <TableHead className="bg-white">Base Price</TableHead>
                      <TableHead className="bg-white">MFD</TableHead>
                      <TableHead className="bg-white">Size</TableHead>
                      <TableHead className="bg-white">Shelf</TableHead>
                      <TableHead className="bg-white">Rack</TableHead>
                      <TableHead className="bg-white">ROL</TableHead>
                      <TableHead className="bg-white">SGST%</TableHead>
                      <TableHead className="bg-white">CGST%</TableHead>
                      <TableHead className="bg-white">IGST%</TableHead>
                      <TableHead className="bg-white">CESS%</TableHead>
                      <TableHead className="bg-white">isTrackable</TableHead>
                      <TableHead className="bg-white">HasSerial</TableHead>
                      <TableHead className="bg-white">Service</TableHead>
                      <TableHead className="text-right sticky right-0 bg-white z-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={imgInInventory ? 28 : 27} className="py-10">
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-800">
                            <div className="text-lg font-semibold mb-1">No Products Available</div>
                            <div className="text-sm">Try adjusting your search criteria or check back later.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                    paginatedProducts.map((product) => (
                      <TableRow
                        key={product.Code}
                        className={`cursor-pointer ${
                          isProductSelected(product.Code)
                            ? 'bg-blue-50 border-2 border-blue-500 shadow-lg transition-all duration-150'
                            : 'transition-all duration-150 hover:bg-gray-50'
                        }`}
                        onClick={() => toggleProductSelection(product)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={isProductSelected(product.Code)}
                            onChange={() => toggleProductSelection(product)}
                          />
                        </TableCell>
                        {imgInInventory && (
                        <TableCell className="sticky left-0 bg-white z-10">
                          <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                            {product.imgStr ? (
                              <img
                                src={getImageUrl(product.imgStr)}
                                alt={product.Title}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  const failedUrl = e.currentTarget.src;
                                  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
                                    console.log('❌ Table row image failed to load:', failedUrl, 'for product:', product.Title);
                                  }
                                  if (!failedUrl.includes('data:image/svg+xml')) {
                                    e.currentTarget.src = getFallbackImage(product.Title);
                                  }
                                }}
                                onLoad={() => {
                                  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
                                    console.log('✅ Table row image loaded successfully for:', product.Title);
                                  }
                                }}
                              />
                            ) : (
                              <Package className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                        </TableCell>
                        )}
                        <TableCell className="font-medium">{product.Title}</TableCell>
                        <TableCell className="break-all max-w-[150px]">
                          <div className="break-all whitespace-normal">{product.Barcode}</div>
                        </TableCell>
                        <TableCell>₹{product.MRP.toFixed(2)}</TableCell>
                        <TableCell>₹{product.SPExclGST?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>₹{product.CostPrice?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>₹{computeOfferPrice(product).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={product.Available > 0 ? "success" : "destructive"}
                            className={
                              product.Available > 0
                                ? "bg-green-50 text-green-700 border-0"
                                : "bg-red-50 text-red-700 border-0"
                            }
                          >
                            {product.Available} units
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-0">
                            {product.Sold || 0} units
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="bg-gray-50 truncate max-w-[100px] text-ellipsis cursor-pointer">
                                {product.Category}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{product.Category}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{product.BrandName || 'N/A'}</TableCell>
                        <TableCell>{product.UQCName || 'N/A'}</TableCell>
                        <TableCell>{product.DiscountPer || '0'}%</TableCell>
                        <TableCell>₹{product.Discount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>₹{(Number(product.BasicPrice) || 0).toFixed(2)}</TableCell>
                        <TableCell>{formatDateDMY(product.Mfd)}</TableCell>
                        <TableCell>{product.Size || 'N/A'}</TableCell>
                        <TableCell>{product.Style || 'N/A'}</TableCell>
                        <TableCell>{product.Stylecode || 'N/A'}</TableCell>
                        <TableCell>{product.MinStQty || 'N/A'}</TableCell>
                        <TableCell>{product.SgstPer?.toFixed(2) || '0.00'}%</TableCell>
                        <TableCell>{product.CGstPer?.toFixed(2) || '0.00'}%</TableCell>
                        <TableCell>{product.IgstPer?.toFixed(2) || '0.00'}%</TableCell>
                        <TableCell>{product.CessPer?.toFixed(2) || '0.00'}%</TableCell>
                        <TableCell>
                          <Badge variant={product.isTraceable ? "success" : "secondary"} className="bg-gray-50 text-gray-700 border-0">
                            {product.isTraceable ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.hasSerial ? "success" : "secondary"} className="bg-gray-50 text-gray-700 border-0">
                            {product.hasSerial ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.isService ? "success" : "secondary"} className="bg-gray-50 text-gray-700 border-0">
                            {product.isService ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right sticky right-0 bg-white z-10">
                          <div className="flex justify-end gap-2">
                            {canEditProduct && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingProduct(product);
                                setShowEditProductModal(true);
                              }}
                              className="text-blue-600 hover:bg-blue-100"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            )}
                            {canDeleteProduct && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProduct(product);
                              }}
                              className="text-red-600 hover:bg-red-100"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        {/* Sticky Horizontal Scrollbar */}
        <div className="hidden lg:block sticky bottom-0 bg-white border-t border-gray-200 p-2 z-30">
          <div 
            className="overflow-x-auto"
            onScroll={(e) => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            {/* <div className="min-w-max h-2 bg-gray-200 rounded-full"> */}
              {/* <div 
                className="h-full bg-blue-500 rounded-full transition-all duration-150"
                style={{ 
                  width: scrollContainerRef.current ? 
                    `${(scrollContainerRef.current.scrollLeft / (scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth)) * 100}%` : 
                    '0%' 
                }}
              />
              </div> */}
            {/* </div> */}
          </div>
        </div>
        </div>

        <div className="hidden lg:flex justify-between items-center mt-2 mb-4">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalProducts)} of {totalProducts} entries
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  tabIndex={currentPage === 1 ? -1 : 0}
                  href="#"
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page, idx, arr) => {
                // Show first, last, current, and neighbors; ellipsis for gaps
                if (
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 1
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                        href="#"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                // Ellipsis logic
                if (
                  (page === currentPage - 2 && page > 1) ||
                  (page === currentPage + 2 && page < totalPages)
                ) {
                  return (
                    <PaginationItem key={`ellipsis-${page}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  tabIndex={currentPage === totalPages ? -1 : 0}
                  href="#"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          <div className="flex items-center gap-2 text-sm text-blue-600 whitespace-nowrap">
                <ChevronLeft className="h-4 w-4" />
                <span>Use scroll buttons or drag to view all columns</span>
                <ChevronRight className="h-4 w-4" />
              </div>
        </div>

        {/* Mobile Scrollable Content */}
        <div className="lg:hidden mt-32 pt-4 pb-4">
          {/* Progress indicator for Android - Console logged instead of displayed */}
          {isAndroid && (
            <div className="hidden">
              {/* Hidden div to trigger console logging */}
              {(() => {
                const currentCount = Math.min(mobileLoadedCount, filteredProducts.length);
                const totalCount = filteredProducts.length;
                console.log(`📱 Android Progress: ${currentCount} of ${totalCount} products`);
                return null;
              })()}
            </div>
          )}
          <div 
            ref={mobileListRef}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4"
          >
            {productsToDisplay.map((product) => (
              <ProductCard
                key={product.Code}
                product={product}
                onViewDetails={handleViewDetails}
                onEdit={(p) => {
                  setEditingProduct(p);
                  setShowEditProductModal(true);
                }}
                onDelete={handleDeleteProduct}
                onUploadImage={(p) => {
                  navigate(`/upload-image?code=${p.Code}`, {
                    state: { productCode: p.Code, productName: p.Title, token },
                  });
                }}
                onUpdateQuantity={(p) => {
                  // Check if product has serial numbers
                  if (p.hasSerial === 1) {
                    setSelectedProductForSerial(p);
                    setShowSerialNumberModal(true);
                  } else {
                    setSelectedProductCode(p.Code);
                    setSelectedProductName(p.Title);
                    setShowUpdateQuantityModal(true);
                  }
                }}
                imgInInventory={imgInInventory}
                canEdit={canEditProduct}
                canDelete={canDeleteProduct}
                canUpdateQuantity={canUpdateQuantityProduct}
              />
            ))}
            
            {/* Loading indicator for lazy loading */}
            {isMobileLoading && (
              <div className="col-span-full flex justify-center items-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">
                  {isAndroid ? 'Loading more products...' : 'Loading more products...'}
                </span>
              </div>
            )}
            
            {/* Near bottom indicator for Android */}
            {isAndroid && hasMoreData && !isMobileLoading && mobileLoadedCount > 0 && (
              <div className="col-span-full text-center py-2 text-xs text-gray-400 pointer-events-none">
                Pull up to load more
              </div>
            )}
            
            {/* End of list indicator */}
            {!hasMoreData && mobileLoadedCount > 0 && (
              <div className="col-span-full text-center py-4 text-sm text-gray-500">
                All products loaded
              </div>
            )}
          </div>
        </div>

        <Dialog open={!!selectedProduct} onOpenChange={(open) => {
          if (!open) {
            setSelectedProduct(null);
          }
        }}>
          <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto p-0">
            {/* Remove any custom close (X) icon here, keep only the default DialogClose */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b">
              <DialogTitle className="text-lg font-semibold truncate max-w-[80%]">{selectedProduct?.Title}</DialogTitle>
            </div>
            <div className="overflow-y-auto px-4 pb-4 pt-2 max-h-[65vh] sm:max-h-[70vh]">
              {selectedProduct?.imgStr && (
                <div className="flex justify-center mb-4">
                  <img
                    src={getImageUrl(selectedProduct.imgStr)}
                    alt={selectedProduct.Title}
                    className="w-40 h-40 object-cover rounded-md border"
                    onError={(e) => {
                      if (!e.currentTarget.src.includes('data:image/svg+xml')) {
                        e.currentTarget.src = getFallbackImage(selectedProduct.Title);
                      }
                    }}
                  />
                </div>
              )}
              <div className="space-y-2 text-sm">
                <p><strong>Product Name:</strong> {selectedProduct?.Product || selectedProduct?.Title}</p>
                <p><strong>Barcode:</strong> <span className="break-words">{selectedProduct?.Barcode}</span></p>
                <p><strong>Category:</strong> {selectedProduct?.Category}</p>
                <p><strong>Available Stock:</strong> {selectedProduct?.Available} units</p>
                <p><strong>Sold:</strong> {selectedProduct?.Sold || 0} units</p>
                <p><strong>MRP:</strong> ₹{selectedProduct?.MRP.toFixed(2)}</p>
                <p><strong>Cost Price:</strong> ₹{selectedProduct?.CostPrice?.toFixed(2) || '0.00'}</p>
                <p><strong>Offer Price:</strong> ₹{computeOfferPrice(selectedProduct).toFixed(2)}</p>
                <p><strong>SGST%:</strong> {selectedProduct?.SgstPer?.toFixed(2) || '0.00'}%</p>
                <p><strong>CGST%:</strong> {selectedProduct?.CGstPer?.toFixed(2) || '0.00'}%</p>
                <p><strong>IGST%:</strong> {selectedProduct?.IgstPer?.toFixed(2) || '0.00'}%</p>
                <p><strong>Cess%:</strong> {selectedProduct?.CessPer?.toFixed(2) || '0.00'}%</p>
                <p><strong>Brand Name:</strong> {selectedProduct?.BrandName || 'N/A'}</p>
                <p><strong>ROL:</strong> {selectedProduct?.MinStQty || 'N/A'}</p>
                <p><strong>Discount%:</strong> {selectedProduct?.DiscountPer || '0'}%</p>
                <p><strong>Discount:</strong> ₹{selectedProduct?.Discount?.toFixed(2) || '0.00'}</p>
                <p><strong>Shelf:</strong> {selectedProduct?.Style || 'N/A'}</p>
                <p><strong>Rack:</strong> {selectedProduct?.Stylecode || 'N/A'}</p>
                <p><strong>UQC:</strong> {selectedProduct?.UQCName || 'N/A'}</p>
                <p><strong>HSN/SAC:</strong> {selectedProduct?.HSN || 'N/A'}</p>
                <p><strong>Size:</strong> {selectedProduct?.Size || 'N/A'}</p>
                <p><strong>Article Code:</strong> {selectedProduct?.Articlecode || 'N/A'}</p>
                <p><strong>Color:</strong> {selectedProduct?.Color || 'N/A'}</p>
                <p><strong>Basic Price:</strong> {selectedProduct?.NetQty || '0.00'}</p>
                <p><strong>Service:</strong> {selectedProduct?.isService ? 'Yes' : 'No'}</p>
                <p><strong>Has Serial Nos.:</strong> {selectedProduct?.hasSerial ? 'Yes' : 'No'}</p>
                <p><strong>Trackable Inventory:</strong> {selectedProduct?.isTraceable ? 'Yes' : 'No'}</p>
                <p><strong>Warranty:</strong> {selectedProduct?.warranty ?? 'N/A'}</p>
                <p><strong>MFD:</strong> {formatDateDMY(selectedProduct?.Mfd)}</p>
                <p><strong>Extra:</strong> {selectedProduct?.Extra || 'N/A'}</p>
              </div>
            </div>
            {/* Cancel button at the bottom for mobile/tablet */}
            <div className="px-4 pb-4 pt-2 sm:hidden">
              <Button variant="outline" className="w-full" onClick={closeProductDetails}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showAddProductModal} onOpenChange={setShowAddProductModal}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
              <DialogDescription>
                Fill in the details to add a new product to your inventory.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <ProductEntryForm
                token={token}
                showFooter={false}
                showServiceCheckbox={showServiceCheckbox}
                showTrackableCheckbox={showTrackableCheckbox}
                showGenderField={false}
                onSuccess={() => {
                  setShowAddProductModal(false);
                  refetch();
                }}
                onCancel={() => setShowAddProductModal(false)}
                ref={productFormRef}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddProductModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="product-form"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => productFormRef.current?.submit()}
              >
                Add Product
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showEditProductModal} onOpenChange={setShowEditProductModal}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Product</DialogTitle>
              <DialogDescription>
                Update the details and click Update to save changes.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {editingProduct && (
                <ProductEntryForm
                  token={token}
                  mode="edit"
                  initialValues={{
                    ...editingProduct,
                    imgStr: editingProduct.imgStr ?? "",
                    NetQty: editingProduct.NetQty ? Number(editingProduct.NetQty) : 0,
                    MinStQty: editingProduct.MinStQty ? Number(editingProduct.MinStQty) : 0,
                    SgstPer: editingProduct.SgstPer ?? 0,
                    CGstPer: editingProduct.CGstPer ?? 0,
                    IgstPer: editingProduct.IgstPer ?? 0,
                    CessPer: editingProduct.CessPer ?? 0,
                    MRP: editingProduct.MRP ?? 0,
                    CostPrice: editingProduct.CostPrice ?? 0,
                    DiscountPer: editingProduct.DiscountPer ? Number(editingProduct.DiscountPer) : 0,
                    Discount: editingProduct.Discount ?? 0,
                    Available: editingProduct.Available ?? 0,
                    hasSerial: !!editingProduct.hasSerial,
                    LocationID: editingProduct.LocationID ? Number(editingProduct.LocationID) : 0,
                  }}
                  showFooter={false}
                  showGenderField={false}
                  onSuccess={() => {
                    setShowEditProductModal(false);
                    setEditingProduct(null);
                    refetch();
                  }}
                  onCancel={() => {
                    setShowEditProductModal(false);
                    setEditingProduct(null);
                  }}
                  ref={productFormRef}
                />
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditProductModal(false);
                  setEditingProduct(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleUpdateProduct}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : 'Update Product'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{productToDelete?.Title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Product'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {showSelectProductSplash && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white border border-blue-200 rounded-lg shadow-lg p-8">
              <div className="text-lg font-semibold text-blue-700 mb-2">Please select a product.</div>
            </div>
          </div>
        )}

        <Dialog open={showUpdateQuantityModal} onOpenChange={setShowUpdateQuantityModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Update Product Quantity</DialogTitle>
              <DialogDescription>
                Update the available quantity for the selected product.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {locations.length > 0 ? (
                <>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="product-title">Product Title</Label>
                      <Input
                        id="product-title"
                        value={selectedProductName || ''}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    
                    {selectedLocation === 'All' ? (
                      <div>
                        <Label htmlFor="location-select">Location</Label>
                        <select
                          id="location-select"
                          value={selectedLocationForUpdate}
                          onChange={(e) => {
                            const selectedLocationId = e.target.value;
                            setSelectedLocationForUpdate(selectedLocationId);
                            
                            // Fetch current quantity for the selected location
                            if (selectedLocationId && selectedProductCode) {
                              fetchCurrentQuantity(selectedProductCode, selectedLocationId);
                            } else {
                              setCurrentQuantity(0);
                            }
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option key={location.ID} value={location.ID}>
                              {location.Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <Label htmlFor="location-display">Location</Label>
                        <Input
                          id="location-display"
                          value={locations.find(loc => loc.ID === selectedLocation)?.Name || ''}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="current-quantity">Current Available Quantity</Label>
                      <Input
                        id="current-quantity"
                        value={isLoadingQuantity ? 'Loading...' : currentQuantity.toString()}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="new-quantity">Update Quantity</Label>
                      <Input
                        id="new-quantity"
                        type="text"
                        inputMode="numeric"
                        value={newQuantityInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Allow only an optional leading '-' followed by digits
                          const validPartial = raw === '' || raw === '-' || /^-?\d+$/.test(raw);
                          if (!validPartial) return; // reject invalid characters
                          setNewQuantityInput(raw);
                        }}
                        onFocus={(e) => {
                          // Auto-select entire value when the field gains focus
                          try { e.currentTarget.select(); } catch {}
                        }}
                        onBlur={() => {
                          // Normalize: lone '-' or empty -> '0'
                          if (newQuantityInput === '' || newQuantityInput === '-') {
                            setNewQuantityInput('0');
                            setNewQuantity(0);
                          } else {
                            const parsed = Number(newQuantityInput);
                            setNewQuantity(isNaN(parsed) ? 0 : parsed);
                          }
                        }}
                        className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter new quantity"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No locations available.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUpdateQuantityModal(false);
                  setNewQuantity(0);
                  setNewQuantityInput('0');
                  setSelectedLocationForUpdate('');
                  setCurrentQuantity(0);
                  setSelectedProductCodes([]);
                  setSelectedProductCode(null);
                  setSelectedProductName(null);
                }}
                disabled={isUpdatingQuantity}
              >
                Cancel
              </Button>
              <Button
                onClick={updateProductQuantity}
                disabled={isUpdatingQuantity || locations.length === 0 || 
                  (selectedLocation === 'All' ? !selectedLocationForUpdate : false)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isUpdatingQuantity ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Excel Progress & Summary Modal */}
        <Dialog open={showImportModal} onOpenChange={(open) => { if (!open) setShowImportModal(false); }}>
          <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Excel</DialogTitle>
              <DialogDescription>
                {importPhase === 'parsing' && 'Reading file and validating...'}
                {importPhase === 'importing' && `Inserted ${importProgress.inserted} out of ${importProgress.total} products`}
                {importPhase === 'done' && importResult &&
                  `Import Completed — ${importResult.successCount} Products Imported — ${importResult.failedRows.length} Failed`}
              </DialogDescription>
            </DialogHeader>
            {importPhase === 'parsing' && (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span>Validating rows...</span>
              </div>
            )}
            {importPhase === 'importing' && (
              <div className="space-y-2 py-4">
                <p className="text-sm text-gray-600">
                  Inserted {importProgress.inserted} out of {importProgress.total} products
                </p>
                <Progress value={importProgress.total ? (importProgress.inserted / importProgress.total) * 100 : 0} className="h-2" />
              </div>
            )}
            {importPhase === 'done' && importResult && (
              <div className="space-y-4 py-2">
                {importResult.duplicateBarcodes.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-medium text-amber-800">Duplicate barcodes detected:</p>
                    <ul className="mt-1 list-inside list-disc text-sm text-amber-700">
                      {importResult.duplicateBarcodes.map(({ barcode, rows }) => (
                        <li key={barcode}>
                          Barcode &quot;{barcode}&quot; — rows: {rows.map(r => `${r.row} (${r.productName})`).join(', ')}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs text-amber-600">See details below. All rows were still sent to the server.</p>
                  </div>
                )}
                {importResult.failedRows.length > 0 && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-medium text-red-800">Failed rows:</p>
                    <ul className="mt-1 max-h-32 overflow-y-auto list-inside list-disc text-sm text-red-700">
                      {importResult.failedRows.slice(0, 15).map((r, i) => (
                        <li key={i}>Row {r.row}: {r.productName} — {r.reason}</li>
                      ))}
                      {importResult.failedRows.length > 15 && (
                        <li className="text-red-500">... and {importResult.failedRows.length - 15} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {importResult.failedRows.length > 0 && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                    <p className="text-sm text-blue-800">
                      Note: The same Excel file was auto-downloaded with two new columns added after the original data:
                      <strong> Status</strong> (Success / Failed) and <strong>Error</strong> (all errors for that row in one cell, comma-separated). Please review those columns to fix and re-import failed rows.
                    </p>
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={() => setShowImportModal(false)}>Close</Button>
                </DialogFooter>
              </div>
            )}
            {importPhase !== 'done' && importPhase !== 'parsing' && importPhase !== 'importing' && (
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Serial Number Modal */}
        {selectedProductForSerial && (
          <AddSerialNumberModal
            isOpen={showSerialNumberModal}
            onClose={() => {
              setShowSerialNumberModal(false);
              setSelectedProductForSerial(null);
            }}
            onRefreshProducts={() => {
              queryClient.invalidateQueries({ queryKey: ['products'] });
            }}
            productCode={selectedProductForSerial.Code}
            productName={selectedProductForSerial.Title}
            productMRP={selectedProductForSerial.MRP}
            productCostPrice={selectedProductForSerial.CostPrice}
            token={token}
            locationId={selectedLocation && selectedLocation !== 'All' ? selectedLocation : undefined}
            defaultExpiryForever={expiryForeverDefault}
          />
        )}
      </div>
    </div>
  );
};

export default InventorySection;